import { createQueue } from "@proj-airi/stream-kit";
import rehypeParse from "rehype-parse";
import rehypeStringify from "rehype-stringify";
import { unified } from "unified";
import { visit } from "unist-util-visit";
import { APICallError } from "@xsai/shared";
import { stepCountAtLeast } from "@xsai/shared-chat";
import { streamText } from "@xsai/stream-text";
import { errorMessageFrom } from "@moeru/std";
import { nanoid } from "nanoid";
import { responses } from "@xsai-ext/responses";
import { nanoid as nanoid$1 } from "nanoid/non-secure";
import { capture, createPushStream, createScope, createSpeechPipeline, observe } from "@proj-airi/pipelines-audio";
//#region src/messages/render-context.ts
function renderHistoryAction(item) {
	if (item.type === "summary") return [
		"Summary:",
		item.text,
		item.fromTurnIndex != null || item.toTurnIndex != null ? `Window: ${item.fromTurnIndex ?? "?"} -> ${item.toTurnIndex ?? "?"}.` : void 0
	].filter(Boolean).join("\n");
	if (item.type === "reaction") return `${item.reactionType}: ${item.text}`;
	if (item.type === "domain-event") return [`Domain event: ${item.eventType}`, JSON.stringify(item.payload, null, 2)].join("\n");
	if (item.action.kind === "text") return item.action.text;
	if (item.action.kind === "event") return `${item.action.name}${item.action.payload ? ` ${JSON.stringify(item.action.payload)}` : ""}`;
	if (item.action.kind === "move-played" || item.action.kind === "move-executed") return `${item.action.kind} ${item.action.san}`;
	return JSON.stringify(item.action);
}
/** Renders domain context as text without assigning a provider role or wire format. */
function renderSegmentText(segment) {
	if (segment.type === "runtime-context") return segment.entries.length ? `\n[Context]\n${segment.entries.map((entry) => `- ${entry.source}: ${entry.text}`).join("\n")}` : "";
	if (segment.type === "text") return segment.text;
	if (segment.type === "instruction") return [segment.priority ? `Instruction [${segment.priority}]:` : "Instruction:", segment.text].join("\n");
	if (segment.type === "tagged-text") return `<${segment.tag}>${segment.text}</${segment.tag}>`;
	if (segment.type === "domain-event") return [`Domain event: ${segment.eventType}`, JSON.stringify(segment.payload, null, 2)].join("\n");
	if (segment.type === "state-snapshot") return [`State snapshot: ${segment.stateType}`, JSON.stringify(segment.payload, null, 2)].join("\n");
	if (segment.type === "summary") return [
		"Summary:",
		segment.text,
		segment.metadata ? JSON.stringify(segment.metadata, null, 2) : void 0
	].filter(Boolean).join("\n");
	if (segment.type === "reference") return [`Reference: ${segment.refType} -> ${segment.targetId}`, segment.note].filter(Boolean).join("\n");
	if (segment.type === "history-block") return segment.items.map(renderHistoryAction).join("\n");
	throw new Error(`Cannot render ${segment.type} as context text`);
}
//#endregion
//#region src/messages/turns.ts
/** Decodes one known model step. Tool results correlate only with calls in this round. */
function readRound(id, entries) {
	const round = {
		id,
		content: [],
		toolInvocations: [],
		projectionIssues: []
	};
	for (const entry of entries) for (const segment of entry.segments) if (segment.type === "tool-call") {
		if (round.toolInvocations.some((call) => call.callId === segment.callId)) {
			round.projectionIssues.push(`Duplicate tool call ${segment.callId} in round ${id}`);
			continue;
		}
		const invocationId = `${id}/${segment.callId}`;
		round.content.push({
			type: "tool",
			invocationId
		});
		round.toolInvocations.push({
			id: invocationId,
			callId: segment.callId,
			name: segment.name,
			arguments: segment.arguments,
			execution: { status: "pending" }
		});
	} else if (segment.type === "tool-result") {
		const invocation = round.toolInvocations.find((call) => call.callId === segment.callId);
		if (invocation) invocation.execution = {
			status: "succeeded",
			output: segment.content
		};
		else round.projectionIssues.push(`Tool result ${segment.callId} has no call in round ${id}`);
	} else if (entry.role === "assistant") {
		if (segment.type !== "image" && segment.type !== "audio" && segment.type !== "file") round.content.push(segment);
	}
	return round;
}
/**
* Reads existing Chat-shaped history at its storage boundary. Each assistant message starts a
* round; following tool results belong to it. Runtime adapters use SDK step boundaries instead.
*/
function readTurns(entries) {
	const turns = [];
	let assistant;
	let pending = [];
	function flush() {
		if (assistant && pending.length) {
			assistant.rounds.push(readRound(`${assistant.id}/${assistant.rounds.length}`, pending));
			pending = [];
		}
	}
	for (const entry of entries) {
		if (entry.role === "assistant" || entry.role === "tool") {
			if (!assistant) {
				assistant = {
					type: "assistant",
					id: entry.id,
					status: "completed",
					rounds: []
				};
				turns.push(assistant);
			}
			if (entry.role === "assistant") flush();
			pending.push(entry);
			continue;
		}
		flush();
		assistant = void 0;
		if (entry.role === "user") turns.push({
			type: "user",
			id: entry.id,
			content: entry.segments
		});
		else turns.push({
			type: "system",
			id: entry.id,
			authority: entry.role === "system" || entry.role === "developer" ? entry.role : "context",
			content: entry.segments
		});
	}
	flush();
	return turns;
}
/** Expands one round for a protocol adapter without duplicating stored calls or results. */
function projectRound(round) {
	if (round.projectionIssues.length) throw new Error(`Round ${round.id} cannot be projected: ${round.projectionIssues.join("; ")}`);
	const entries = [];
	const segments = [];
	for (const part of round.content) {
		if (part.type !== "tool") {
			segments.push(part);
			continue;
		}
		const invocation = round.toolInvocations.find((call) => call.id === part.invocationId);
		if (!invocation) throw new Error(`Missing tool invocation ${part.invocationId}`);
		segments.push({
			type: "tool-call",
			callId: invocation.callId,
			name: invocation.name,
			arguments: invocation.arguments
		});
	}
	if (segments.length) entries.push({
		id: round.id,
		role: "assistant",
		segments
	});
	for (const invocation of round.toolInvocations) if (invocation.execution.status === "succeeded" || invocation.execution.status === "failed") entries.push({
		id: invocation.id,
		role: "tool",
		segments: [{
			type: "tool-result",
			callId: invocation.callId,
			content: invocation.execution.output
		}]
	});
	return entries;
}
/** Application context becomes user-level data; trusted instructions retain their declared authority. */
function projectInput(turn) {
	if (turn.type === "user") return {
		id: turn.id,
		role: "user",
		segments: turn.content
	};
	return {
		id: turn.id,
		role: turn.authority === "context" ? "context" : turn.authority,
		segments: turn.content
	};
}
//#endregion
//#region src/messages/chat-completions.ts
/**
* Converts Chat content at storage and SDK boundaries without inventing a message envelope.
*
* @example
* chatContentToInputSegments('hello')
* // => [{ type: 'text', text: 'hello' }]
*/
function chatContentToInputSegments(content) {
	if (content == null) return [];
	if (typeof content === "string") return [{
		type: "text",
		text: content
	}];
	return content.map((part) => {
		switch (part.type) {
			case "text": return {
				type: "text",
				text: part.text
			};
			case "image_url": return {
				type: "image",
				url: part.image_url.url,
				detail: part.image_url.detail
			};
			case "input_audio": return {
				type: "audio",
				...part.input_audio
			};
			case "file":
				if (part.file.file_data !== void 0 && part.file.file_id === void 0) return {
					type: "file",
					data: part.file.file_data,
					name: part.file.filename
				};
				if (part.file.file_id !== void 0 && part.file.file_data === void 0) return {
					type: "file",
					providerFileId: part.file.file_id,
					name: part.file.filename
				};
				throw new Error("Chat file requires exactly one source");
		}
		throw new Error("Unsupported Chat content part");
	});
}
/**
* Reads Chat-shaped storage or SDK output into portable message semantics.
* This is an ingress boundary; Responses never calls the Chat request renderer.
*
* @example
* chatMessagesToProjectionEntries([{ role: 'user', content: 'Hello' }])[0].segments
* // => [{ type: 'text', text: 'Hello' }]
*/
function chatMessagesToProjectionEntries(messages, idPrefix = "message") {
	return messages.map((message, index) => {
		const id = `${idPrefix}-${index}`;
		if (message.role === "error") return {
			id,
			role: "user",
			segments: [{
				type: "text",
				text: `User encountered error: ${message.content}`
			}]
		};
		if (message.role === "tool") return {
			id,
			role: "tool",
			segments: [{
				type: "tool-result",
				callId: message.tool_call_id,
				content: chatContentToInputSegments(message.content)
			}]
		};
		if (message.role === "assistant") {
			const segments = typeof message.content === "string" ? [{
				type: "text",
				text: message.content
			}] : message.content?.map((part) => part.type === "text" ? {
				type: "text",
				text: part.text
			} : {
				type: "refusal",
				text: part.refusal
			}) ?? [];
			if (message.refusal) segments.push({
				type: "refusal",
				text: message.refusal
			});
			for (const call of message.tool_calls ?? []) {
				if (call.function.name == null || call.function.arguments == null) throw new Error("Cannot replay an unfinished function call");
				segments.push({
					type: "tool-call",
					callId: call.id,
					name: call.function.name,
					arguments: call.function.arguments
				});
			}
			return {
				id,
				role: "assistant",
				segments
			};
		}
		if (message.role === "system" || message.role === "developer") return {
			id,
			role: message.role,
			segments: typeof message.content === "string" ? [{
				type: "text",
				text: message.content
			}] : message.content.map((part) => ({
				type: "text",
				text: part.text
			}))
		};
		return {
			id,
			role: message.role,
			segments: chatContentToInputSegments(message.content)
		};
	});
}
/** Reads persisted Chat records; the storage identity scopes turn, round, and invocation ids. */
function chatMessagesToTurns(messages, idPrefix) {
	return readTurns(chatMessagesToProjectionEntries(messages, idPrefix));
}
function writeContent(segment) {
	switch (segment.type) {
		case "image": return {
			type: "image_url",
			image_url: {
				url: segment.url,
				detail: segment.detail
			}
		};
		case "audio": return {
			type: "input_audio",
			input_audio: {
				data: segment.data,
				format: segment.format
			}
		};
		case "file":
			if (segment.url) throw new Error("Chat Completions does not support file URLs");
			return {
				type: "file",
				file: {
					file_data: segment.data,
					filename: segment.name,
					file_id: segment.providerFileId
				}
			};
		default: return {
			type: "text",
			text: renderSegmentText(segment)
		};
	}
}
/**
* Converts Chat content for providers that accept only strings.
*
* @example
* chatContentToString([{ type: 'text', text: 'Hi' }, { type: 'refusal', refusal: 'No' }])
* // => 'HiNo'
*/
function chatContentToString(content) {
	return content.map((part) => part.type === "text" ? part.text : part.type === "refusal" ? part.refusal : "").join("");
}
function renderEntry(message, supportsContentArray) {
	const result = [];
	const role = message.role === "context" || message.role === "event" || message.role === "summary" ? "user" : message.role;
	let parts = [];
	let assistantParts = [];
	let calls = [];
	function flush() {
		if (role === "assistant") {
			if (assistantParts.length || calls.length) {
				const content = !supportsContentArray || assistantParts.every((part) => part.type === "text") ? assistantParts.map((part) => part.type === "text" ? part.text : part.refusal).join("") : assistantParts;
				result.push({
					role,
					content,
					...calls.length ? { tool_calls: calls } : {}
				});
			}
		} else if (parts.length) {
			if (role === "tool") throw new Error("Tool messages require a correlated tool result");
			if (role === "system" || role === "developer") {
				if (parts.some((part) => part.type !== "text")) throw new Error(`${role} messages require text content`);
				result.push({
					role,
					content: parts.map((part) => part.type === "text" ? part.text : "").join("")
				});
			} else {
				const content = !supportsContentArray || parts.every((part) => part.type === "text") ? parts.map((part) => part.type === "text" ? part.text : "").join("") : parts;
				result.push({
					role,
					content
				});
			}
		}
		parts = [];
		assistantParts = [];
		calls = [];
	}
	for (const segment of message.segments) if (segment.type === "tool-result") {
		flush();
		const content = segment.content.map(writeContent);
		result.push({
			role: "tool",
			tool_call_id: segment.callId,
			content: !supportsContentArray || content.every((part) => part.type === "text") ? content.map((part) => part.type === "text" ? part.text : "").join("") : content
		});
	} else if (segment.type === "tool-call") {
		if (role !== "assistant") throw new Error("Only assistant messages can invoke tools");
		calls.push({
			type: "function",
			id: segment.callId,
			function: {
				name: segment.name,
				arguments: segment.arguments
			}
		});
	} else {
		if (calls.length) flush();
		if (role === "assistant") {
			if (segment.type === "refusal") assistantParts.push({
				type: "refusal",
				refusal: segment.text
			});
			else assistantParts.push({
				type: "text",
				text: renderSegmentText(segment)
			});
		} else parts.push(writeContent(segment));
	}
	flush();
	return result;
}
/**
* Projects context directly into Chat Completions messages.
* Array compatibility applies only here. Only matching Chat continuation can bypass portable projection.
* Pure-text arrays become strings. String-only mode also preserves refusal text and omits media.
* Input messages and provider extension fields remain unchanged.
*/
function conversationToChatMessages(conversation, supportsContentArray = true, scope) {
	return conversation.turns.flatMap((turn) => {
		if (turn.type !== "assistant") return renderEntry(projectInput(turn), supportsContentArray);
		return turn.rounds.flatMap((round) => {
			const continuation = round.continuation;
			if (scope && continuation?.protocol === "chat-completions" && continuation.scope === scope) {
				if (!Array.isArray(continuation.data)) throw new Error("Chat continuation must contain a message array");
				return continuation.data.map((message) => {
					if (Array.isArray(message.content) && (!supportsContentArray || message.content.every((part) => part.type === "text"))) return {
						...message,
						content: chatContentToString(message.content)
					};
					return message;
				});
			}
			return projectRound(round).flatMap((entry) => renderEntry(entry, supportsContentArray));
		});
	});
}
//#endregion
//#region src/messages/context-prompt.ts
/**
* Render runtime context modules into a compact, readable text block.
*
* Use when:
* - Composing chat prompts that need to attach side-channel runtime context
*   (e.g. game state, system status) to the latest user message.
*
* Expects:
* - A snapshot keyed by `contextId`. Only the per-message `text` field is
*   included; volatile metadata (random IDs, ms timestamps) is excluded so
*   the output stays deterministic and KV-cache-friendly.
*
* Returns:
* - Empty string when the snapshot is empty.
* - Otherwise a `[Context]` block with one bullet per module, e.g.
*   `[Context]\n- system:minecraft-integration: Bot is online ...`
*
* Why this shape (not XML):
* - Weak local models (8B/14B) tend to mirror conspicuous structured
*   wrappers (`<context>...</context>`) back into their replies, treating
*   them as data to be quoted. A flat bullet list looks like ordinary
*   narrative, which suppresses that mirroring tendency.
* - See: https://github.com/moeru-ai/airi/issues/1539
*/
function formatContextPromptText(contextsSnapshot) {
	const entries = Object.entries(contextsSnapshot);
	if (entries.length === 0) return "";
	const lines = entries.flatMap(([contextId, messages]) => messages.map((m) => `- ${contextId}: ${m.text}`));
	if (lines.length === 0) return "";
	return ["[Context]", ...lines].join("\n");
}
/**
* Builds a user-role context prompt message from active runtime context.
*
* Use when:
* - A caller needs the historical standalone context prompt shape.
*
* Expects:
* - Context messages have already been bucketed and cloned by the context registry.
*
* Returns:
* - `null` when no prompt text is available.
* - A user message carrying the rendered context text otherwise.
*/
function buildContextPromptMessage(contextsSnapshot) {
	const promptText = formatContextPromptText(contextsSnapshot);
	if (!promptText) return null;
	return {
		role: "user",
		content: [{
			type: "text",
			text: promptText
		}]
	};
}
//#endregion
//#region src/messages/datetime-prefix.ts
function padDatePart(value) {
	return value.toString().padStart(2, "0");
}
/**
* Formats a timestamp as `[YYYY-MM-DD HH:MM] ` in the user's local timezone.
*
* Use when:
* - Annotating user messages so the model has a concrete time anchor on
*   every turn — historic and current user turns use the same shape so that
*   prefix-cache stays valid when a "current" turn becomes "historic" on
*   the next send.
* - Not used for assistant messages — that caused the model to mirror the
*   prefix into its own output.
*
* Returns:
* - String including a trailing space, e.g. `"[2026-04-25 18:47] "`.
*
* Before:
* - createdAt = 1745570820000  (a Unix ms in Asia/Shanghai)
*
* After:
* - "[2026-04-25 18:47] "
*/
function formatTimePrefix(createdAt) {
	const date = new Date(createdAt);
	return `[${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())} ${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}] `;
}
//#endregion
//#region src/messages/preview.ts
function describeSegment(segment) {
	switch (segment.type) {
		case "tool-call": return `${segment.name}(${segment.arguments})`;
		case "tool-result": return segment.content.map(describeSegment).join("");
		case "image": return "[Image]";
		case "audio": return "[Audio]";
		case "file": return `[File: ${segment.name ?? "attachment"}]`;
		case "refusal": return segment.text;
		default: return renderSegmentText(segment);
	}
}
/**
* Builds text-only hook and devtools records, excluding native state and media payloads.
* Media becomes a label, including media in tool results. These records must not feed inference.
*/
function renderConversationPreview(context) {
	return context.turns.flatMap((turn) => {
		if (turn.type !== "assistant") {
			const entry = projectInput(turn);
			return [{
				role: entry.role === "system" || entry.role === "developer" ? entry.role : "user",
				content: entry.segments.map(describeSegment).join("")
			}];
		}
		return turn.rounds.map((round) => ({
			role: "assistant",
			content: round.content.map((part) => {
				if (part.type !== "tool") return describeSegment(part);
				const invocation = round.toolInvocations.find((call) => call.id === part.invocationId);
				if (!invocation) return "[Unknown tool]";
				const output = invocation.execution.status === "succeeded" || invocation.execution.status === "failed" ? invocation.execution.output.map(describeSegment).join("") : `[${invocation.execution.status}]`;
				return `${invocation.name}(${invocation.arguments}): ${output}`;
			}).join("")
		}));
	});
}
//#endregion
//#region src/runtime/agent-hooks.ts
function createChatHooks() {
	const onBeforeMessageComposedHooks = [];
	const onAfterMessageComposedHooks = [];
	const onBeforeSendHooks = [];
	const onAfterSendHooks = [];
	const onTokenLiteralHooks = [];
	const onTokenSpecialHooks = [];
	const onStreamEndHooks = [];
	const onAssistantResponseEndHooks = [];
	const onAssistantMessageHooks = [];
	const onChatTurnCompleteHooks = [];
	function onBeforeMessageComposed(cb) {
		onBeforeMessageComposedHooks.push(cb);
		return () => {
			const index = onBeforeMessageComposedHooks.indexOf(cb);
			if (index >= 0) onBeforeMessageComposedHooks.splice(index, 1);
		};
	}
	function onAfterMessageComposed(cb) {
		onAfterMessageComposedHooks.push(cb);
		return () => {
			const index = onAfterMessageComposedHooks.indexOf(cb);
			if (index >= 0) onAfterMessageComposedHooks.splice(index, 1);
		};
	}
	function onBeforeSend(cb) {
		onBeforeSendHooks.push(cb);
		return () => {
			const index = onBeforeSendHooks.indexOf(cb);
			if (index >= 0) onBeforeSendHooks.splice(index, 1);
		};
	}
	function onAfterSend(cb) {
		onAfterSendHooks.push(cb);
		return () => {
			const index = onAfterSendHooks.indexOf(cb);
			if (index >= 0) onAfterSendHooks.splice(index, 1);
		};
	}
	function onTokenLiteral(cb) {
		onTokenLiteralHooks.push(cb);
		return () => {
			const index = onTokenLiteralHooks.indexOf(cb);
			if (index >= 0) onTokenLiteralHooks.splice(index, 1);
		};
	}
	function onTokenSpecial(cb) {
		onTokenSpecialHooks.push(cb);
		return () => {
			const index = onTokenSpecialHooks.indexOf(cb);
			if (index >= 0) onTokenSpecialHooks.splice(index, 1);
		};
	}
	function onStreamEnd(cb) {
		onStreamEndHooks.push(cb);
		return () => {
			const index = onStreamEndHooks.indexOf(cb);
			if (index >= 0) onStreamEndHooks.splice(index, 1);
		};
	}
	function onAssistantResponseEnd(cb) {
		onAssistantResponseEndHooks.push(cb);
		return () => {
			const index = onAssistantResponseEndHooks.indexOf(cb);
			if (index >= 0) onAssistantResponseEndHooks.splice(index, 1);
		};
	}
	function onAssistantMessage(cb) {
		onAssistantMessageHooks.push(cb);
		return () => {
			const index = onAssistantMessageHooks.indexOf(cb);
			if (index >= 0) onAssistantMessageHooks.splice(index, 1);
		};
	}
	function onChatTurnComplete(cb) {
		onChatTurnCompleteHooks.push(cb);
		return () => {
			const index = onChatTurnCompleteHooks.indexOf(cb);
			if (index >= 0) onChatTurnCompleteHooks.splice(index, 1);
		};
	}
	function clearHooks() {
		onBeforeMessageComposedHooks.length = 0;
		onAfterMessageComposedHooks.length = 0;
		onBeforeSendHooks.length = 0;
		onAfterSendHooks.length = 0;
		onTokenLiteralHooks.length = 0;
		onTokenSpecialHooks.length = 0;
		onStreamEndHooks.length = 0;
		onAssistantResponseEndHooks.length = 0;
		onAssistantMessageHooks.length = 0;
		onChatTurnCompleteHooks.length = 0;
	}
	async function emitBeforeMessageComposedHooks(message, context) {
		for (const hook of onBeforeMessageComposedHooks) await hook(message, context);
	}
	async function emitAfterMessageComposedHooks(message, context) {
		for (const hook of onAfterMessageComposedHooks) await hook(message, context);
	}
	async function emitBeforeSendHooks(message, context) {
		for (const hook of onBeforeSendHooks) await hook(message, context);
	}
	async function emitAfterSendHooks(message, context) {
		for (const hook of onAfterSendHooks) await hook(message, context);
	}
	async function emitTokenLiteralHooks(literal, context) {
		for (const hook of onTokenLiteralHooks) await hook(literal, context);
	}
	async function emitTokenSpecialHooks(special, context) {
		for (const hook of onTokenSpecialHooks) await hook(special, context);
	}
	async function emitStreamEndHooks(context) {
		for (const hook of onStreamEndHooks) await hook(context);
	}
	async function emitAssistantResponseEndHooks(message, context) {
		for (const hook of onAssistantResponseEndHooks) await hook(message, context);
	}
	async function emitAssistantMessageHooks(message, messageText, context) {
		for (const hook of onAssistantMessageHooks) await hook(message, messageText, context);
	}
	async function emitChatTurnCompleteHooks(chat, context) {
		for (const hook of onChatTurnCompleteHooks) await hook(chat, context);
	}
	return {
		onBeforeMessageComposed,
		onAfterMessageComposed,
		onBeforeSend,
		onAfterSend,
		onTokenLiteral,
		onTokenSpecial,
		onStreamEnd,
		onAssistantResponseEnd,
		onAssistantMessage,
		onChatTurnComplete,
		emitBeforeMessageComposedHooks,
		emitAfterMessageComposedHooks,
		emitBeforeSendHooks,
		emitAfterSendHooks,
		emitTokenLiteralHooks,
		emitTokenSpecialHooks,
		emitStreamEndHooks,
		emitAssistantResponseEndHooks,
		emitAssistantMessageHooks,
		emitChatTurnCompleteHooks,
		clearHooks
	};
}
//#endregion
//#region src/runtime/llm-marker-parser.ts
const TAG_OPEN = "<|";
const TAG_CLOSE = "|>";
const ESCAPED_TAG_OPEN = "<{'|'}";
const ESCAPED_TAG_CLOSE = "{'|'}>";
function createPushStream$1() {
	let closed = false;
	let controller = null;
	return {
		stream: new ReadableStream({
			start(ctrl) {
				controller = ctrl;
			},
			cancel() {
				closed = true;
			}
		}),
		write(value) {
			if (!controller || closed) return;
			controller.enqueue(value);
		},
		close() {
			if (!controller || closed) return;
			closed = true;
			controller.close();
		},
		error(err) {
			if (!controller || closed) return;
			closed = true;
			controller.error(err);
		}
	};
}
async function readStream(stream, handler) {
	const reader = stream.getReader();
	try {
		while (true) {
			const { value, done } = await reader.read();
			if (done) break;
			await handler(value);
		}
	} finally {
		reader.releaseLock();
	}
}
function createLlmMarkerParser(options) {
	const minLiteralEmitLength = Math.max(1, options?.minLiteralEmitLength ?? 1);
	const tailLength = Math.max(1, 5);
	let buffer = "";
	let inTag = false;
	return {
		async consume(textPart, onLiteral, onSpecial) {
			buffer += textPart;
			buffer = buffer.replaceAll(ESCAPED_TAG_OPEN, TAG_OPEN).replaceAll(ESCAPED_TAG_CLOSE, TAG_CLOSE);
			while (buffer.length > 0) if (!inTag) {
				const openTagIndex = buffer.indexOf(TAG_OPEN);
				if (openTagIndex < 0) {
					if (buffer.length - tailLength >= minLiteralEmitLength) {
						const emit = buffer.slice(0, -5);
						buffer = buffer.slice(-5);
						await onLiteral(emit);
					}
					break;
				}
				if (openTagIndex > 0) {
					const emit = buffer.slice(0, openTagIndex);
					buffer = buffer.slice(openTagIndex);
					await onLiteral(emit);
				}
				inTag = true;
			} else {
				const closeTagIndex = buffer.indexOf(TAG_CLOSE);
				if (closeTagIndex < 0) break;
				const emit = buffer.slice(0, closeTagIndex + 2);
				buffer = buffer.slice(closeTagIndex + 2);
				await onSpecial(emit);
				inTag = false;
			}
		},
		async end(onLiteral) {
			if (!inTag && buffer.length > 0) {
				await onLiteral(buffer);
				buffer = "";
			}
		}
	};
}
function createLlmMarkerStream(input, options) {
	const { stream, write, close, error } = createPushStream$1();
	const parser = createLlmMarkerParser(options);
	readStream(input, async (chunk) => {
		await parser.consume(chunk, async (literal) => {
			if (!literal) return;
			write({
				type: "literal",
				value: literal
			});
		}, async (special) => {
			write({
				type: "special",
				value: special
			});
		});
	}).then(async () => {
		await parser.end(async (literal) => {
			if (!literal) return;
			write({
				type: "literal",
				value: literal
			});
		});
		close();
	}).catch((err) => {
		error(err);
	});
	return stream;
}
/**
* Creates a streaming parser for LLM responses with AIRI special markers.
*
* Use when:
* - Handling streamed model output that may contain `<|...|>` markers.
* - Literal text and special marker tokens need to be emitted separately.
*
* Expects:
* - Callers feed chunks in order and call `end()` once the model stream ends.
*
* Returns:
* - A parser with `consume()` and `end()` methods.
*/
function useLlmmarkerParser(options) {
	let fullText = "";
	const { stream, write, close } = createPushStream$1();
	const processing = readStream(createLlmMarkerStream(stream, { minLiteralEmitLength: options.minLiteralEmitLength }), async (token) => {
		if (token.type === "literal") await options.onLiteral?.(token.value);
		if (token.type === "special") await options.onSpecial?.(token.value);
	});
	return {
		/**
		* Consumes a chunk of text from the stream.
		*
		* @param textPart The chunk of text to consume.
		*/
		async consume(textPart) {
			fullText += textPart;
			write(textPart);
		},
		/**
		* Finalizes the parsing process.
		* Any remaining content in the buffer is flushed as a final literal part.
		*/
		async end() {
			close();
			await processing;
			await options.onEnd?.(fullText);
		}
	};
}
//#endregion
//#region src/runtime/response-categoriser.ts
/**
* Maps tag names to categories
* All tags are treated as reasoning (filtered from TTS)
*/
function mapTagNameToCategory(_tagName) {
	return "reasoning";
}
/**
* Extracts all XML-like tags from a response using rehype pipeline
* Works with any tag format: <tag>content</tag>
* Only extracts tags that are actually complete (have closing tags in source)
*/
function extractAllTags(response) {
	const tags = [];
	try {
		const tree = unified().use(rehypeParse, { fragment: true }).parse(response);
		visit(tree, "element", (node) => {
			const position = node.position;
			if (!position?.start || !position?.end) return;
			const startIndex = getOffsetFromPosition(response, position.start);
			const endIndex = getOffsetFromPosition(response, position.end);
			if (startIndex === -1 || endIndex === -1) return;
			const fullMatch = response.slice(startIndex, endIndex);
			const expectedClosingTag = `</${node.tagName}>`;
			if (!fullMatch.includes(expectedClosingTag)) return;
			tags.push({
				tagName: node.tagName,
				content: extractTextContent(node),
				fullMatch,
				startIndex,
				endIndex
			});
		});
	} catch (error) {
		console.error("Failed to parse response for tag extraction:", error);
	}
	return tags;
}
/**
* Converts a position (line/column) to a character offset in the string
*/
function getOffsetFromPosition(text, position) {
	if (!position || typeof position.line !== "number" || typeof position.column !== "number") return -1;
	const lines = text.split("\n");
	let offset = 0;
	for (let i = 0; i < position.line - 1 && i < lines.length; i++) offset += lines[i].length + 1;
	offset += position.column - 1;
	return offset;
}
/**
* Extracts text content from an element node
*/
function extractTextContent(node) {
	const textParts = [];
	if (node.children) {
		for (const child of node.children) if (child.type === "text") textParts.push(child.value);
		else if (child.type === "element") textParts.push(extractTextContent(child));
	}
	return textParts.join("");
}
/**
* Categorizes a model response by dynamically extracting any XML-like tags
* Works with any tag format the model uses
*/
function categorizeResponse(response, _providerId) {
	const extractedTags = extractAllTags(response);
	if (extractedTags.length === 0) return {
		segments: [],
		speech: response,
		reasoning: "",
		raw: response
	};
	const segments = extractedTags.map((tag) => ({
		category: mapTagNameToCategory(tag.tagName),
		content: tag.content.trim(),
		startIndex: tag.startIndex,
		endIndex: tag.endIndex,
		raw: tag.fullMatch,
		tagName: tag.tagName
	}));
	segments.sort((a, b) => a.startIndex - b.startIndex);
	const speechParts = [];
	let lastEnd = 0;
	for (const segment of segments) {
		if (segment.startIndex > lastEnd) {
			const text = response.slice(lastEnd, segment.startIndex).trim();
			if (text) speechParts.push(text);
		}
		lastEnd = Math.max(lastEnd, segment.endIndex);
	}
	if (lastEnd < response.length) {
		const text = response.slice(lastEnd).trim();
		if (text) speechParts.push(text);
	}
	const reasoning = segments.filter((s) => s.category === "reasoning").map((s) => s.content).join("\n\n");
	return {
		segments,
		speech: speechParts.join(" ").trim() || "",
		reasoning,
		raw: response
	};
}
/**
* Note: This receives literal text from useLlmmarkerParser (special tokens <|...|> are already extracted).
* Only XML/HTML tags like <think>, <reasoning> need to be parsed here.
*/
function createStreamingCategorizer(providerId, onSegment) {
	let buffer = "";
	let categorized = null;
	let lastEmittedSegmentIndex = -1;
	let lastParsedLength = 0;
	let tagState = "outside";
	let tagStackDepth = 0;
	function checkIncompleteTag() {
		try {
			const tree = unified().use(rehypeParse, { fragment: true }).parse(buffer);
			const stringified = unified().use(rehypeStringify).stringify(tree).toString();
			if (stringified !== buffer) return buffer.trim().slice(-30) !== stringified.trim().slice(-30);
			return false;
		} catch {
			return true;
		}
	}
	function processChunkIncrementally(chunk) {
		let tagJustClosed = false;
		for (let i = 0; i < chunk.length; i++) {
			const char = chunk[i];
			switch (tagState) {
				case "outside":
					if (char === "<") {
						if (i + 1 < chunk.length && chunk[i + 1] === "/") {
							tagState = "in-closing-tag";
							i++;
						} else tagState = "in-opening-tag";
					}
					break;
				case "in-opening-tag":
					if (char === ">") {
						tagState = "in-content";
						tagStackDepth++;
					}
					break;
				case "in-content":
					if (char === "<") {
						if (i + 1 < chunk.length && chunk[i + 1] === "/") {
							tagState = "in-closing-tag";
							i++;
						} else tagState = "in-opening-tag";
					}
					break;
				case "in-closing-tag": if (char === ">") {
					tagStackDepth--;
					if (tagStackDepth === 0) {
						tagState = "outside";
						tagJustClosed = true;
					} else tagState = "in-content";
				}
			}
		}
		return tagJustClosed;
	}
	return {
		consume(chunk) {
			const tagJustClosed = processChunkIncrementally(chunk);
			buffer += chunk;
			if (!categorized || tagJustClosed || buffer.length - lastParsedLength > 1e3) {
				categorized = categorizeResponse(buffer, providerId);
				lastParsedLength = buffer.length;
			}
			if (!categorized) {
				categorized = categorizeResponse(buffer, providerId);
				lastParsedLength = buffer.length;
			}
			if (onSegment && categorized.segments.length > 0) for (let i = lastEmittedSegmentIndex + 1; i < categorized.segments.length; i++) {
				const segment = categorized.segments[i];
				if (buffer.length >= segment.endIndex) {
					onSegment(segment);
					lastEmittedSegmentIndex = i;
				}
			}
		},
		/**
		* Checks if the current position in the stream is part of speech content
		* Returns true if the text should be sent to TTS
		*/
		isSpeechAt(position) {
			if (!categorized || categorized.segments.length === 0) return true;
			for (const segment of categorized.segments) if (position >= segment.startIndex && position < segment.endIndex) return false;
			return true;
		},
		/**
		* Filters text to only include speech parts
		* Removes content that falls within thought/reasoning segments
		*/
		filterToSpeech(text, startPosition) {
			if (checkIncompleteTag()) {
				const fullText = buffer + text;
				try {
					const tree = unified().use(rehypeParse, { fragment: true }).parse(fullText);
					let closingOffset = -1;
					visit(tree, "element", (node) => {
						const position = node.position;
						if (position?.end && closingOffset === -1) {
							const endOffset = getOffsetFromPosition(fullText, position.end);
							const elementSource = fullText.slice(getOffsetFromPosition(fullText, position.start), endOffset);
							const expectedClosingTag = `</${node.tagName}>`;
							if (elementSource.includes(expectedClosingTag)) {
								if (endOffset >= buffer.length && endOffset <= fullText.length) closingOffset = endOffset - buffer.length;
							}
						}
					});
					if (closingOffset === -1) return "";
					text = text.slice(closingOffset);
					startPosition += closingOffset;
					categorized = categorizeResponse(buffer, providerId);
				} catch {
					return "";
				}
			}
			if (!categorized || categorized.segments.length === 0) return text;
			let filtered = "";
			const endPosition = startPosition + text.length;
			const overlappingSegments = categorized.segments.filter((segment) => segment.endIndex > startPosition && segment.startIndex < endPosition);
			if (overlappingSegments.length === 0) return text;
			let currentPos = startPosition;
			for (const segment of overlappingSegments) {
				const segmentStart = Math.max(segment.startIndex, startPosition);
				const segmentEnd = Math.min(segment.endIndex, endPosition);
				if (segmentStart > currentPos) {
					const beforeStart = currentPos - startPosition;
					const beforeEnd = segmentStart - startPosition;
					filtered += text.slice(beforeStart, beforeEnd);
				}
				currentPos = segmentEnd;
			}
			if (currentPos < endPosition) {
				const afterStart = currentPos - startPosition;
				filtered += text.slice(afterStart);
			}
			return filtered;
		},
		getCurrentPosition() {
			return buffer.length;
		},
		end() {
			return categorizeResponse(buffer, providerId);
		},
		getCurrent() {
			return categorized;
		}
	};
}
//#endregion
//#region src/runtime/chat-orchestrator-runtime.ts
const REASONING_UI_FLUSH_CHUNK_SIZE = 24;
/**
* Caps repeated reply text in the model prompt. The referenced message remains
* in history, so the prefix only needs enough text to identify it.
*/
const REPLY_PROMPT_REFERENCE_CHARACTER_LIMIT = 480;
function prependTextToContent(msg, text) {
	const content = msg.content;
	if (content === void 0) return {
		...msg,
		content: text
	};
	if (typeof content === "string") return {
		...msg,
		content: `${text}${content}`
	};
	if (Array.isArray(content)) {
		const first = content[0];
		if (first && first.type === "text" && typeof first.text === "string") {
			const next = [{
				...first,
				text: `${text}${first.text}`
			}, ...content.slice(1)];
			return {
				...msg,
				content: next
			};
		}
		return {
			...msg,
			content: [{
				type: "text",
				text
			}, ...content]
		};
	}
	return msg;
}
function getMessageText(message) {
	if (typeof message.content === "string") return message.content;
	if (!Array.isArray(message.content)) return "";
	return message.content.filter((part) => part.type === "text").map((part) => part.text).join("\n");
}
/**
* Formats a model-only reference to the message selected by the user.
*
* @example
* formatReplyPromptPrefix('message-1', new Map([
*   ['message-1', { id: 'message-1', role: 'user', content: 'Earlier turn' }],
* ]))
* // => '[Replying to: Earlier turn]\n'
*/
function formatReplyPromptPrefix(replyToMessageId, messagesById) {
	if (!replyToMessageId) return "";
	const target = messagesById.get(replyToMessageId);
	if (!target) return "";
	const targetText = getMessageText(target).replace(/\s+/g, " ").trim();
	const preview = targetText.length > REPLY_PROMPT_REFERENCE_CHARACTER_LIMIT ? `${targetText.slice(0, 479).trimEnd()}…` : targetText;
	return preview ? `[Replying to: ${preview}]\n` : `[Replying to message: ${replyToMessageId}]\n`;
}
function resolveReplyTargetId(replyToMessageId, messages) {
	if (!replyToMessageId) return void 0;
	return messages.some((message) => message.id === replyToMessageId) ? replyToMessageId : void 0;
}
function cloneStreamingMessage(message) {
	try {
		return structuredClone(message);
	} catch {
		return JSON.parse(JSON.stringify(message));
	}
}
function hasAssistantOutput(message) {
	return message.slices.length > 0 || message.tool_results.length > 0 || (message.citations?.length ?? 0) > 0 || !!message.categorization?.reasoning.trim();
}
function defaultCreateId() {
	return globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
/** Releases the request owner on abort even when an external provider does not settle. Late provider output remains signal-guarded. */
async function waitForProvider(work, signal) {
	const cancelled = Promise.withResolvers();
	const abort = () => cancelled.reject(signal.reason);
	signal.addEventListener("abort", abort, { once: true });
	if (signal.aborted) abort();
	try {
		await Promise.race([work, cancelled.promise]);
	} finally {
		signal.removeEventListener("abort", abort);
	}
}
/**
* Creates the core chat orchestrator runtime used behind UI facades.
*
* Use when:
* - A platform wants AIRI chat send orchestration without Vue/Pinia coupling.
* - Session, context, foreground stream, and LLM integrations are provided as adapters.
*
* Expects:
* - Session messages are returned in chronological order.
* - `foregroundStream.patch` replaces the visible streaming assistant message.
*
* Returns:
* - A runtime with send queue APIs, hook registry, writable sending state, and queue snapshots.
*/
function createChatOrchestratorRuntime(deps) {
	const activeSends = /* @__PURE__ */ new Map();
	const sendQueues = /* @__PURE__ */ new Map();
	const hooks = createChatHooks();
	const now = deps.now ?? (() => Date.now());
	const monotonicNow = deps.monotonicNow ?? (() => globalThis.performance?.now?.() ?? Date.now());
	const createId = deps.createId ?? defaultCreateId;
	const unwrapMessage = deps.unwrapMessage ?? ((message) => message);
	let sending = false;
	let activeSendSessionId;
	let activeStreamingMessage;
	let pendingQueuedSends = [];
	function emitStateChange() {
		deps.onStateChange?.({
			activeTurns: [...activeSends].flatMap(([sessionId, active]) => active.turnId && active.message ? [{
				sessionId,
				turnId: active.turnId,
				message: active.message
			}] : []),
			sending,
			activeSendSessionId,
			activeStreamingMessage,
			pendingQueuedSendCount: pendingQueuedSends.length
		});
	}
	function setSending(next) {
		const nextActiveSendSessionId = next ? activeSendSessionId ?? deps.getActiveSessionId() : void 0;
		if (sending === next && activeSendSessionId === nextActiveSendSessionId) return;
		sending = next;
		activeSendSessionId = nextActiveSendSessionId;
		if (!next) activeStreamingMessage = void 0;
		emitStateChange();
	}
	function isForegroundSession(sessionId) {
		return sessionId === deps.getActiveSessionId();
	}
	function beginStream(sessionId, message) {
		const active = activeSends.get(sessionId);
		if (!active) return;
		active.message = cloneStreamingMessage(message);
		sending = true;
		activeSendSessionId = sessionId;
		activeStreamingMessage = cloneStreamingMessage(message);
		emitStateChange();
		if (isForegroundSession(sessionId)) deps.foregroundStream.patch(cloneStreamingMessage(message));
	}
	function updateStream(sessionId, message) {
		const active = activeSends.get(sessionId);
		if (!active) return;
		active.message = cloneStreamingMessage(message);
		if (sessionId === activeSendSessionId) activeStreamingMessage = active.message;
		emitStateChange();
		if (isForegroundSession(sessionId)) deps.foregroundStream.patch(cloneStreamingMessage(message));
	}
	function resetForegroundStream(sessionId) {
		if (isForegroundSession(sessionId)) deps.foregroundStream.reset();
	}
	function ingestRuntimeContexts() {
		for (const provider of deps.runtimeContextProviders ?? []) {
			const contextMessage = provider();
			if (contextMessage) deps.context.ingest(contextMessage);
		}
	}
	function getStablePromptTimestamp(message, fallbackCreatedAt) {
		if (typeof message.createdAt === "number") return message.createdAt;
		message.createdAt = fallbackCreatedAt;
		return fallbackCreatedAt;
	}
	function buildContext(history) {
		const nowTs = now();
		const messagesById = new Map(history.flatMap((message) => message.id ? [[message.id, message]] : []));
		return { turns: history.flatMap((message, historyIndex) => {
			if (message.role === "assistant" && message.generationTranscript) return [structuredClone(unwrapMessage(message.generationTranscript))];
			const source = message.role === "user" ? prependTextToContent(unwrapMessage(message), `${formatTimePrefix(getStablePromptTimestamp(message, nowTs))}${formatReplyPromptPrefix(message.replyToMessageId, messagesById)}`) : unwrapMessage(message);
			return chatMessagesToTurns(source.role === "assistant" && source.providerTranscript?.length ? source.providerTranscript : [source], message.id ?? `history-${historyIndex}`);
		}) };
	}
	async function performSend(sendingMessage, options, generation, sessionId, abortSignal, activeProvider, accepted) {
		if (!sendingMessage && !options.attachments?.length) return;
		deps.session.ensureSession(sessionId);
		const existingSessionMessages = deps.session.getSessionMessages(sessionId);
		let replyToMessageId = resolveReplyTargetId(options.replyToMessageId, existingSessionMessages);
		const turnIndex = existingSessionMessages.filter((message) => message.role === "user").length + 1;
		const isActivationAttempt = !existingSessionMessages.some((message) => message.role === "assistant" && !message.interrupted);
		ingestRuntimeContexts();
		const sendingCreatedAt = now();
		const streamContextMessageId = createId();
		const assistantMessageId = createId();
		const roundId = options.messageId ?? createId();
		const streamingMessageContext = {
			sessionId,
			turnId: roundId,
			message: {
				role: "user",
				content: sendingMessage,
				createdAt: sendingCreatedAt,
				id: streamContextMessageId,
				...replyToMessageId ? { replyToMessageId } : {}
			},
			contexts: deps.context.snapshot(),
			composedMessage: [],
			input: options.input
		};
		deps.onLifecycle?.({
			phase: "before-compose",
			channel: "chat",
			sessionId,
			textPreview: sendingMessage,
			details: { contexts: streamingMessageContext.contexts }
		});
		const isStaleGeneration = () => deps.session.getSessionGeneration(sessionId) !== generation;
		const shouldAbort = () => isStaleGeneration() || abortSignal.aborted;
		if (shouldAbort()) return;
		const buildingMessage = {
			role: "assistant",
			content: "",
			slices: [],
			tool_results: [],
			createdAt: now(),
			id: assistantMessageId
		};
		activeSends.get(sessionId).turnId = roundId;
		beginStream(sessionId, buildingMessage);
		const hasVoice = options.input?.type === "input:voice" || options.input?.type === "input:text:voice";
		const sendSource = hasVoice ? "voice" : "text";
		const correlation = {
			conversationId: sessionId,
			roundId,
			turnIndex
		};
		deps.onTrackFirstMessage?.();
		if (isActivationAttempt) deps.onChatActivationStarted?.({
			...correlation,
			source: sendSource,
			model: options.model,
			provider: activeProvider
		});
		deps.onMessageSendStarted?.({
			...correlation,
			source: sendSource,
			model: options.model
		});
		const roundStartedAt = monotonicNow();
		let assistantStored = false;
		let generationCompleted = false;
		let failed = false;
		try {
			await hooks.emitBeforeMessageComposedHooks(sendingMessage, streamingMessageContext);
			const contentParts = [{
				type: "text",
				text: sendingMessage
			}];
			if (options.attachments) for (const attachment of options.attachments) {
				if (attachment.type === "audio") contentParts.push({
					type: "input_audio",
					input_audio: {
						data: attachment.data,
						format: attachment.mimeType === "audio/wav" ? "wav" : "mp3"
					}
				});
				if (attachment.type === "image") contentParts.push({
					type: "image_url",
					image_url: { url: `data:${attachment.mimeType};base64,${attachment.data}` }
				});
			}
			const finalContent = contentParts.length > 1 ? contentParts : sendingMessage;
			if (!streamingMessageContext.input) streamingMessageContext.input = {
				type: "input:text",
				data: { text: sendingMessage }
			};
			if (shouldAbort()) return;
			replyToMessageId = resolveReplyTargetId(options.replyToMessageId, deps.session.getSessionMessages(sessionId));
			if (replyToMessageId) streamingMessageContext.message.replyToMessageId = replyToMessageId;
			else delete streamingMessageContext.message.replyToMessageId;
			const userMessage = {
				role: "user",
				content: finalContent,
				createdAt: sendingCreatedAt,
				id: roundId,
				...replyToMessageId ? { replyToMessageId } : {},
				...options.toolReferences?.length ? { tools: options.toolReferences } : {}
			};
			const receipt = await deps.session.commitUserMessage(sessionId, userMessage);
			accepted.resolve({
				sessionId,
				messageId: receipt.messageId
			});
			if (receipt.status === "existing" || shouldAbort()) {
				resetForegroundStream(sessionId);
				return;
			}
			deps.onUserMessageAppended?.({
				sessionId,
				message: userMessage,
				messageText: sendingMessage,
				source: sendSource,
				model: options.model,
				provider: activeProvider,
				roundId,
				turnIndex
			});
			const sessionMessagesForSend = deps.session.getSessionMessages(sessionId);
			deps.onUserTurnReady?.({
				sessionId,
				messageText: sendingMessage,
				sessionMessages: sessionMessagesForSend
			});
			const categorizer = createStreamingCategorizer(activeProvider);
			let streamPosition = 0;
			const stickers = options.stickers;
			let stickerEmitted = false;
			const parser = useLlmmarkerParser({
				onLiteral: async (literal) => {
					if (shouldAbort()) return;
					categorizer.consume(literal);
					const speechOnly = categorizer.filterToSpeech(literal, streamPosition);
					streamPosition += literal.length;
					if (speechOnly.trim()) {
						buildingMessage.content += speechOnly;
						await hooks.emitTokenLiteralHooks(speechOnly, streamingMessageContext);
						if (shouldAbort()) return;
						const lastSlice = buildingMessage.slices.at(-1);
						if (lastSlice?.type === "text") lastSlice.text += speechOnly;
						else buildingMessage.slices.push({
							type: "text",
							text: speechOnly
						});
						updateStream(sessionId, buildingMessage);
					}
				},
				onSpecial: async (special) => {
					if (shouldAbort()) return;
					if (/^<\|STICKER\b/.test(special)) {
						if (!categorizer.filterToSpeech(special, streamPosition)) return;
						const id = /^<\|STICKER ([a-z0-9-]+)\|>$/.exec(special)?.[1];
						if (!stickerEmitted && id && stickers?.some((sticker) => sticker.id === id)) {
							buildingMessage.slices.push({
								type: "sticker",
								stickerId: id
							});
							stickerEmitted = true;
							updateStream(sessionId, buildingMessage);
						}
						return;
					}
					await hooks.emitTokenSpecialHooks(special, streamingMessageContext);
				},
				onEnd: async (fullText) => {
					if (shouldAbort()) return;
					const finalCategorization = categorizeResponse(fullText.replace(/<(?:\||\{'\|'\})STICKER\b[\s\S]*?(?:(?:\||\{'\|'\})>|$)/g, ""), activeProvider);
					const reasoningContentField = buildingMessage.categorization?.reasoning?.trim();
					buildingMessage.categorization = {
						speech: finalCategorization.speech,
						reasoning: reasoningContentField || finalCategorization.reasoning
					};
					updateStream(sessionId, buildingMessage);
				},
				minLiteralEmitLength: 1
			});
			const toolCallQueue = createQueue({ handlers: [async (ctx) => {
				if (shouldAbort()) return;
				if (ctx.data.type === "tool-call") {
					buildingMessage.slices.push(ctx.data);
					updateStream(sessionId, buildingMessage);
					return;
				}
				if (ctx.data.type === "tool-call-result") {
					buildingMessage.tool_results.push(ctx.data);
					updateStream(sessionId, buildingMessage);
				}
			}] });
			const context = buildContext(sessionMessagesForSend);
			const stickerPrompt = stickers?.length ? [
				"You can send one optional sticker per reply with a marker from this catalog.",
				"Use stickers when the user requests one or when a lighthearted response fits. Avoid them in serious conversations.",
				"Keep your text complete. Never invent sticker IDs or image URLs.",
				"Choose the ID whose name and emotion tags best fit the conversation.",
				...stickers.map((sticker) => `<|STICKER ${sticker.id}|>: ${sticker.description}`)
			].join("\n") : "";
			const systemPromptSupplement = [options.systemPromptSupplement?.trim(), stickerPrompt].filter(Boolean).join("\n\n");
			if (systemPromptSupplement) {
				const systemMessage = context.turns.find((turn) => turn.type === "system" && turn.authority === "system");
				if (systemMessage?.type === "system") systemMessage.content.push({
					type: "text",
					text: `\n\n${systemPromptSupplement}`
				});
				else context.turns.unshift({
					id: "system-supplement",
					type: "system",
					authority: "system",
					content: [{
						type: "text",
						text: systemPromptSupplement
					}]
				});
			}
			const contextsSnapshot = deps.context.snapshot();
			const entries = Object.entries(contextsSnapshot).flatMap(([source, messages]) => messages.map((message) => ({
				source,
				text: message.text
			})));
			if (entries.length) {
				const lastMessage = context.turns.at(-1);
				if (lastMessage?.type === "user") lastMessage.content.push({
					type: "runtime-context",
					entries
				});
				deps.onLifecycle?.({
					phase: "prompt-context-built",
					channel: "chat",
					sessionId,
					details: { contexts: contextsSnapshot }
				});
			}
			streamingMessageContext.composedMessage = renderConversationPreview(context);
			deps.onPromptProjection?.({
				sessionId,
				message: sendingMessage,
				contexts: contextsSnapshot,
				composedMessage: streamingMessageContext.composedMessage
			});
			deps.onLifecycle?.({
				phase: "after-compose",
				channel: "chat",
				sessionId,
				textPreview: sendingMessage,
				details: { composedMessage: streamingMessageContext.composedMessage }
			});
			await hooks.emitAfterMessageComposedHooks(sendingMessage, streamingMessageContext);
			await hooks.emitBeforeSendHooks(sendingMessage, streamingMessageContext);
			let fullText = "";
			const headers = options.providerConfig?.headers || {};
			if (shouldAbort()) return;
			const llmRequestStartedAt = monotonicNow();
			let llmFirstTokenEmitted = false;
			let generationUsage = { source: "unavailable" };
			let generatedTurn;
			deps.onLlmRequestStarted?.({
				...correlation,
				model: options.model,
				provider: activeProvider || "unknown",
				hasVoice
			});
			await waitForProvider(deps.llm.stream(options.model, options.chatProvider, context, {
				headers,
				providerId: activeProvider,
				supportsAudioInput: options.supportsAudioInput,
				supportsVisionInput: options.supportsVisionInput,
				abortSignal,
				onGeneratedTurn: (turn) => {
					generatedTurn = structuredClone(turn);
				},
				requestCorrelation: {
					conversationId: correlation.conversationId,
					turnId: correlation.roundId
				},
				tools: options.tools,
				temperature: options.temperature,
				topP: options.topP,
				waitForTools: true,
				onUsage: (usage) => {
					if (shouldAbort()) return;
					generationUsage = usage;
					deps.onLlmGeneration?.({
						...correlation,
						model: options.model,
						provider: activeProvider,
						inputTokens: usage.inputTokens,
						outputTokens: usage.outputTokens,
						totalTokens: usage.totalTokens,
						usageSource: usage.source
					});
				},
				onStreamEvent: async (event) => {
					if (shouldAbort()) return;
					switch (event.type) {
						case "search":
							buildingMessage.search = {
								id: event.id,
								status: event.status
							};
							updateStream(sessionId, buildingMessage);
							break;
						case "citations":
							buildingMessage.citations = [...buildingMessage.citations ?? [], ...event.citations];
							updateStream(sessionId, buildingMessage);
							break;
						case "tool-call":
							toolCallQueue.enqueue({
								type: "tool-call",
								toolCall: event
							});
							break;
						case "tool-result":
							toolCallQueue.enqueue({
								type: "tool-call-result",
								id: event.toolCallId,
								result: event.result
							});
							break;
						case "tool-error":
							toolCallQueue.enqueue({
								type: "tool-call-result",
								id: event.toolCallId,
								isError: true,
								result: event.result
							});
							break;
						case "text-delta":
							if (!llmFirstTokenEmitted) {
								llmFirstTokenEmitted = true;
								deps.onLlmFirstToken?.({
									...correlation,
									model: options.model,
									ttfbMs: Math.round(monotonicNow() - llmRequestStartedAt)
								});
							}
							fullText += event.text;
							await parser.consume(event.text);
							break;
						case "reasoning-delta": {
							if (shouldAbort()) return;
							const { reasoning = "" } = buildingMessage.categorization ?? {};
							const nextReasoning = reasoning + event.text;
							buildingMessage.categorization = {
								speech: typeof buildingMessage.content === "string" ? buildingMessage.content : "",
								reasoning: nextReasoning
							};
							const crossesBoundary = Math.floor(nextReasoning.length / REASONING_UI_FLUSH_CHUNK_SIZE) > Math.floor(reasoning.length / REASONING_UI_FLUSH_CHUNK_SIZE);
							if (!reasoning || crossesBoundary) updateStream(sessionId, buildingMessage);
							break;
						}
						case "finish": break;
						case "error": throw event.error ?? /* @__PURE__ */ new Error("Stream error");
					}
				}
			}), abortSignal);
			if (shouldAbort()) return;
			await parser.end();
			if (shouldAbort()) return;
			generationCompleted = true;
			buildingMessage.generationTranscript = generatedTurn;
			try {
				deps.onAssistantResponseRendered?.({
					...correlation,
					model: options.model,
					latencyMs: Math.round(monotonicNow() - llmRequestStartedAt)
				});
			} catch (error) {
				console.error("Assistant response observer failed:", error);
			}
			if (!shouldAbort() && (buildingMessage.slices.length > 0 || generatedTurn?.rounds.length)) {
				const finalAssistant = buildingMessage;
				deps.session.appendSessionMessage(sessionId, finalAssistant);
				assistantStored = true;
				deps.onAssistantMessageAppended?.({
					sessionId,
					roundId,
					message: finalAssistant,
					messageText: fullText
				});
			}
			if (shouldAbort()) return;
			await hooks.emitStreamEndHooks(streamingMessageContext);
			if (shouldAbort()) return;
			await hooks.emitAssistantResponseEndHooks(fullText, streamingMessageContext);
			if (shouldAbort()) return;
			await hooks.emitAfterSendHooks(sendingMessage, streamingMessageContext);
			if (shouldAbort()) return;
			await hooks.emitAssistantMessageHooks({ ...buildingMessage }, fullText, streamingMessageContext);
			if (shouldAbort()) return;
			await hooks.emitChatTurnCompleteHooks({
				output: { ...buildingMessage },
				outputText: fullText,
				toolCalls: sessionMessagesForSend.filter((msg) => msg.role === "tool")
			}, streamingMessageContext);
			if (shouldAbort()) return;
			deps.onAssistantTurnReady?.({
				sessionId,
				messageText: fullText,
				sessionMessages: sessionMessagesForSend
			});
			resetForegroundStream(sessionId);
			const durationMs = Math.round(monotonicNow() - roundStartedAt);
			deps.onMessageRound?.({
				...correlation,
				durationMs,
				hasVoice,
				model: options.model,
				inputTokens: generationUsage.inputTokens,
				outputTokens: generationUsage.outputTokens,
				totalTokens: generationUsage.totalTokens,
				usageSource: generationUsage.source
			});
			if (isActivationAttempt) deps.onChatActivationSucceeded?.({
				...correlation,
				durationMs,
				source: sendSource,
				model: options.model,
				provider: activeProvider
			});
		} catch (error) {
			if (shouldAbort()) return;
			failed = true;
			if (!assistantStored && !generationCompleted && hasAssistantOutput(buildingMessage)) deps.session.appendSessionMessage(sessionId, {
				...cloneStreamingMessage(buildingMessage),
				interrupted: true
			});
			resetForegroundStream(sessionId);
			console.error("Error sending message:", error);
			deps.onMessageRoundFailed?.({
				...correlation,
				source: sendSource,
				model: options.model,
				provider: activeProvider,
				failureStage: "llm_response",
				errorCode: "llm_response_failed"
			});
			if (isActivationAttempt) deps.onChatActivationFailed?.({
				...correlation,
				source: sendSource,
				model: options.model,
				provider: activeProvider,
				failureStage: "llm_response",
				errorCode: "llm_response_failed"
			});
			throw error;
		} finally {
			if (!assistantStored && !generationCompleted && abortSignal.aborted && !isStaleGeneration() && hasAssistantOutput(buildingMessage)) {
				deps.session.appendSessionMessage(sessionId, {
					...cloneStreamingMessage(buildingMessage),
					interrupted: true
				});
				resetForegroundStream(sessionId);
			}
			activeSends.delete(sessionId);
			sending = activeSends.size > 0;
			if (activeSendSessionId === sessionId) {
				activeSendSessionId = activeSends.keys().next().value;
				activeStreamingMessage = activeSendSessionId ? activeSends.get(activeSendSessionId)?.message : void 0;
			}
			emitStateChange();
			deps.onSendSettled?.({
				sessionId,
				turnId: roundId,
				status: shouldAbort() ? "cancelled" : failed ? "failed" : "finished"
			});
		}
	}
	/** Each session owns its FIFO. A blocked provider cannot prevent another session from starting. */
	function queueFor(sessionId) {
		const existing = sendQueues.get(sessionId);
		if (existing) return existing;
		const sendQueue = createQueue({ handlers: [async ({ data }) => {
			const { sendingMessage, options, generation, deferred, sessionId, cancelled, providerId } = data;
			if (cancelled) return;
			if (deps.session.getSessionGeneration(sessionId) !== generation) {
				const error = /* @__PURE__ */ new Error("Chat session was reset before send could start");
				data.accepted.reject(error);
				deferred.reject(error);
				return;
			}
			const controller = new AbortController();
			activeSends.set(sessionId, { controller });
			try {
				await performSend(sendingMessage, options, generation, sessionId, options.signal ? AbortSignal.any([controller.signal, options.signal]) : controller.signal, providerId, data.accepted);
				deferred.resolve();
			} catch (error) {
				data.accepted.reject(error);
				deferred.reject(error);
			} finally {
				data.accepted.reject(/* @__PURE__ */ new Error("Chat request ended before persistence"));
				activeSends.delete(sessionId);
			}
		}] });
		sendQueue.on("enqueue", (queuedSend) => {
			pendingQueuedSends.push(queuedSend);
			emitStateChange();
		});
		sendQueue.on("dequeue", (queuedSend) => {
			pendingQueuedSends = pendingQueuedSends.filter((item) => item !== queuedSend);
			emitStateChange();
		});
		sendQueue.on("drain", () => {
			if (sendQueues.get(sessionId) === sendQueue) sendQueues.delete(sessionId);
		});
		sendQueues.set(sessionId, sendQueue);
		return sendQueue;
	}
	function submit(sendingMessage, options, targetSessionId) {
		const sessionId = targetSessionId ?? deps.getActiveSessionId();
		const generation = deps.session.getSessionGeneration(sessionId);
		const accepted = Promise.withResolvers();
		const deferred = Promise.withResolvers();
		accepted.promise.catch(() => {});
		deferred.promise.catch(() => {});
		queueFor(sessionId).enqueue({
			providerId: options.providerId ?? deps.getActiveProvider?.() ?? "",
			sendingMessage,
			options: {
				...options,
				systemPromptSupplement: options.systemPromptSupplement ?? deps.getSystemPromptSupplement?.(),
				stickers: options.stickers?.map(({ id, description }) => ({
					id,
					description
				}))
			},
			generation,
			sessionId,
			accepted,
			deferred
		});
		return {
			accepted: accepted.promise,
			done: deferred.promise
		};
	}
	function ingest(sendingMessage, options, targetSessionId) {
		return submit(sendingMessage, options, targetSessionId).done;
	}
	function cancelPendingSends(sessionId) {
		for (const [activeSessionId, active] of activeSends) if (!sessionId || sessionId === activeSessionId) active.controller.abort(/* @__PURE__ */ new Error("Chat session send was cancelled"));
		for (const queued of pendingQueuedSends) {
			if (sessionId && queued.sessionId !== sessionId) continue;
			queued.cancelled = true;
			const error = /* @__PURE__ */ new Error("Chat session was reset before send could start");
			queued.accepted.reject(error);
			queued.deferred.reject(error);
		}
		pendingQueuedSends = sessionId ? pendingQueuedSends.filter((item) => item.sessionId !== sessionId) : [];
		emitStateChange();
	}
	function cancelTurn(turn) {
		if (activeSends.get(turn.sessionId)?.turnId === turn.turnId) activeSends.get(turn.sessionId)?.controller.abort(/* @__PURE__ */ new Error("Chat turn was cancelled"));
		for (const queued of pendingQueuedSends) {
			if (queued.sessionId !== turn.sessionId || queued.options.messageId !== turn.turnId) continue;
			queued.cancelled = true;
			const error = /* @__PURE__ */ new Error("Chat turn was cancelled before it started");
			queued.accepted.reject(error);
			queued.deferred.reject(error);
		}
		pendingQueuedSends = pendingQueuedSends.filter((queued) => !queued.cancelled);
		emitStateChange();
	}
	function getPendingQueuedSendSnapshot() {
		return pendingQueuedSends.map((queued) => ({
			sessionId: queued.sessionId,
			generation: queued.generation,
			cancelled: !!queued.cancelled,
			messagePreview: queued.sendingMessage.slice(0, 120),
			hasAttachments: !!queued.options.attachments?.length,
			inputType: queued.options.input?.type
		}));
	}
	return {
		cancelTurn,
		submit,
		ingest,
		cancelPendingSends,
		getPendingQueuedSendSnapshot,
		getPendingQueuedSendCount: () => pendingQueuedSends.length,
		getSending: () => sending,
		setSending,
		hooks
	};
}
//#endregion
//#region src/runtime/context-registry.ts
const CONTEXT_UPDATE_REPLACE_SELF = "replace-self";
const CONTEXT_UPDATE_APPEND_SELF = "append-self";
function formatMetadataSource(source) {
	if (!source) return void 0;
	if ("extension" in source) return `${source.extension.id}:${source.id}`;
	return source.id;
}
function defaultGetSourceKey(event, fallback = "unknown") {
	return formatMetadataSource(event.metadata?.source) ?? event.source ?? fallback;
}
/**
* Creates a context registry that owns active buckets and bounded ingest history.
*
* Use when:
* - Runtime contexts need replace-self or append-self bucket semantics.
* - UI or transport layers need cloned snapshots without owning mutation policy.
*
* Expects:
* - Context messages are structured-cloneable before they enter the registry.
* - Unknown strategies should still be recorded in history for observability.
*
* Returns:
* - A registry whose snapshots cannot mutate internal active bucket state.
*/
function createContextRegistry(options = {}) {
	const historyLimit = options.historyLimit ?? 400;
	const getSourceKey = options.getSourceKey ?? defaultGetSourceKey;
	let currentActiveContexts = /* @__PURE__ */ new Map();
	let currentContextHistory = [];
	function ingest(envelope) {
		const sourceKey = getSourceKey(envelope);
		const safeEnvelopeToStore = structuredClone(envelope);
		if (!currentActiveContexts.has(sourceKey)) currentActiveContexts.set(sourceKey, []);
		let result;
		if (envelope.strategy === CONTEXT_UPDATE_REPLACE_SELF) {
			currentActiveContexts.set(sourceKey, [safeEnvelopeToStore]);
			result = {
				sourceKey,
				mutation: "replace",
				entryCount: currentActiveContexts.get(sourceKey)?.length ?? 0
			};
		} else if (envelope.strategy === CONTEXT_UPDATE_APPEND_SELF) {
			currentActiveContexts.get(sourceKey)?.push(safeEnvelopeToStore);
			result = {
				sourceKey,
				mutation: "append",
				entryCount: currentActiveContexts.get(sourceKey)?.length ?? 0
			};
		}
		currentContextHistory = [...currentContextHistory, {
			...safeEnvelopeToStore,
			sourceKey
		}].slice(-historyLimit);
		return result;
	}
	function reset() {
		currentActiveContexts = /* @__PURE__ */ new Map();
		currentContextHistory = [];
	}
	function snapshot() {
		return Object.fromEntries(Array.from(currentActiveContexts, ([sourceKey, messages]) => [sourceKey, structuredClone(messages)]));
	}
	return {
		ingest,
		reset,
		snapshot,
		activeContexts: snapshot,
		contextHistory: () => structuredClone(currentContextHistory)
	};
}
//#endregion
//#region src/runtime/generation.ts
/** A generation is owned by its caller's turn; anonymous callers receive an independent identity. */
function createAssistantTurn(turnId, runId) {
	return {
		type: "assistant",
		id: turnId ?? nanoid(),
		runId,
		status: "completed",
		rounds: []
	};
}
/**
* Saves the native step even when its portable content is unknown. A later protocol change reports
* projectionIssues instead of silently omitting data. SDK tool failures stay attached to their call.
*/
function recordRound(turn, native, step, model, roundOffset, decode) {
	const id = `${turn.id}/${roundOffset + turn.rounds.length}`;
	const entries = [];
	const issues = [];
	for (const item of native.data) try {
		entries.push(...decode(item));
	} catch (error) {
		issues.push(errorMessageFrom(error) ?? "Unknown native content");
	}
	const round = readRound(id, entries);
	round.projectionIssues.push(...issues);
	round.modelCall = {
		model,
		finishReason: step.finishReason,
		usage: step.usage
	};
	round.continuation = native;
	for (const result of step.toolResults) {
		const invocation = round.toolInvocations.find((call) => call.callId === result.toolCallId);
		if (invocation && result.isError && invocation.execution.status === "succeeded") invocation.execution = {
			...invocation.execution,
			status: "failed"
		};
	}
	turn.rounds.push(round);
	return round;
}
/**
* Owns step boundaries and produces a turn only after all model and tool steps settle.
* Each adapter supplies its native envelope and portable projection policy.
*/
function createGeneration(input) {
	const starts = [];
	const models = [];
	/** SDK snapshots mark step starts; only offsets remain owned by this generation. */
	function prepareStep({ input: current, model }) {
		starts.push(current.length);
		models.push(model ?? input.model);
		return {};
	}
	async function complete(items, steps) {
		const [final, completedSteps] = await Promise.all([items, steps]);
		const turn = createAssistantTurn(input.turnId, input.runId);
		for (const [index, step] of completedSteps.entries()) {
			const start = starts[index];
			if (start === void 0) throw new Error("Missing SDK model step boundary");
			const output = final.slice(start, starts[index + 1] ?? final.length);
			recordRound(turn, input.continuation(output, index), step, models[index] ?? input.model, input.roundOffset ?? 0, input.project);
		}
		const lastStep = completedSteps.at(-1);
		if ((lastStep?.finishReason === "tool-calls" || lastStep?.finishReason === "tool_calls") && lastStep.toolCalls.length > 0 && lastStep.toolResults.length === 0) throw new Error("Generation tool step limit reached");
		return turn;
	}
	return {
		prepareStep,
		complete
	};
}
//#endregion
//#region src/runtime/request-context.ts
/** Removes provider fields that the next request no longer defines. */
function replaceProviderConfig(target, previousKeys, next) {
	for (const key of previousKeys) if (!Object.hasOwn(next, key)) Reflect.deleteProperty(target, key);
	Object.assign(target, next);
	return Object.keys(next);
}
/** Builds a compatibility-cache key from the same configuration used by the request. */
function modelKey(model, { protocol, config }) {
	return `${protocol === "responses" ? "responses:" : ""}${config.baseURL}-${model}`;
}
/** Applies a caller override before the learned tool compatibility for this exact model request. */
function supportsTools(model, request, options) {
	return options?.supportsTools ?? options?.toolsCompatibility?.get(modelKey(model, request)) !== false;
}
/** Applies a caller override before the learned Chat content compatibility for this exact model request. */
function supportsContentArray(model, request, options) {
	return options?.supportsContentArray ?? options?.contentArrayCompatibility?.get(modelKey(model, request)) !== false;
}
/**
* Request overrides replace configured headers without regard to casing.
* Authorization keeps the SDK spelling so it also replaces the SDK apiKey default.
*/
function mergeRequestHeaders(configured, overrides) {
	const headers = new Headers(configured);
	new Headers(overrides).forEach((value, name) => headers.set(name, value));
	return Object.fromEntries(Array.from(headers, ([name, value]) => [name === "authorization" ? "Authorization" : name, value]));
}
/**
* Native continuation belongs to one provider identity, endpoint, model and conversation.
* Request credentials and headers do not participate in this persisted scope.
*/
function createContinuationScope(config, options) {
	return JSON.stringify([
		options?.providerId,
		String(config.baseURL),
		config.model,
		options?.requestCorrelation?.conversationId
	]);
}
//#endregion
//#region src/runtime/request-switch.ts
/** Carries completed tool rounds to a new continuation scope without persisting a partial turn. */
var RequestSwitch = class extends Error {
	next;
	partialTurn;
	constructor(next, partialTurn) {
		super("Model request scope changed");
		this.next = next;
		this.partialTurn = partialTurn;
	}
};
//#endregion
//#region src/runtime/xsai-events.ts
/**
* Maps xsAI stream events onto the AIRI {@link StreamEvent} contract.
*
* xsAI 0.5.0-beta.8 marks failed tool executions with `isError: true` on
* `tool-result.done` instead of aborting the stream, so AIRI can distinguish
* `tool-error` from `tool-result` directly from the event payload.
*/
function toAiriStreamEvent(event) {
	switch (event.type) {
		case "text.delta": return {
			type: "text-delta",
			text: event.delta
		};
		case "reasoning.delta": return {
			type: "reasoning-delta",
			text: event.delta
		};
		case "tool-call.done": return {
			...event,
			type: "tool-call"
		};
		case "tool-result.done":
			if (event.isError === true) return {
				type: "tool-error",
				toolCallId: event.toolCallId,
				result: typeof event.result === "string" ? event.result : JSON.stringify(event.result),
				isError: true
			};
			return {
				type: "tool-result",
				toolCallId: event.toolCallId,
				result: typeof event.result === "string" || Array.isArray(event.result) ? event.result : JSON.stringify(event.result)
			};
		case "error": return {
			type: "error",
			error: event.cause ?? new Error(event.message)
		};
		case "text.start":
		case "text.done":
		case "reasoning.start":
		case "reasoning.done":
		case "step.start":
		case "step.done":
		case "tool-call.start":
		case "tool-call.delta": return null;
	}
}
//#endregion
//#region src/runtime/chat-completions.ts
/** Projects one context snapshot and returns only the newly generated turn. */
function streamChatCompletions(input) {
	const messages = conversationToChatMessages(input.conversation, input.supportsContentArray, input.scope);
	const scopes = [];
	const generation = createGeneration({
		turnId: input.options?.requestCorrelation?.turnId ?? input.options?.generationTurnId,
		runId: input.options?.requestCorrelation?.runId,
		model: input.config.model,
		roundOffset: input.options?.generationRoundOffset,
		continuation: (data, index) => ({
			protocol: "chat-completions",
			scope: scopes[index] ?? input.scope,
			data
		}),
		project: (item) => chatMessagesToProjectionEntries([item])
	});
	let providerConfigKeys = Object.keys(input.config);
	const requestOptions = {
		...input.config,
		prepareStep: ({ input: current, steps }) => {
			const resolveStep = input.options?.resolveStep;
			if (!resolveStep) {
				scopes.push(input.scope);
				return generation.prepareStep({ input: current });
			}
			return (async () => {
				const firstStep = scopes.length === 0 && input.initialStep;
				const next = firstStep || await resolveStep();
				const nextRequest = firstStep ? {
					protocol: "chat-completions",
					config: input.config
				} : next.chatProvider.generation(next.model);
				const nextScope = createContinuationScope(nextRequest.config, {
					...input.options,
					providerId: next.providerId
				});
				if (nextRequest.protocol !== "chat-completions" || nextScope !== input.scope) throw new RequestSwitch(next, await generation.complete(Promise.resolve(current), Promise.resolve(steps)));
				const toolsSupported = supportsTools(next.model, nextRequest, input.options);
				providerConfigKeys = replaceProviderConfig(requestOptions, providerConfigKeys, nextRequest.config);
				Object.assign(requestOptions, {
					apiKey: nextRequest.config.apiKey,
					fetch: nextRequest.config.fetch,
					temperature: next.temperature,
					topP: next.topP,
					headers: mergeRequestHeaders(nextRequest.config.headers, next.headers),
					tools: toolsSupported && next.tools?.length ? next.tools : void 0,
					toolChoice: void 0
				});
				generation.prepareStep({
					input: current,
					model: next.model
				});
				const contentArraySupported = supportsContentArray(next.model, nextRequest, input.options);
				scopes.push(nextScope);
				if (!contentArraySupported) for (const [index, message] of current.entries()) {
					if (!Array.isArray(message.content)) continue;
					current[index] = {
						...message,
						content: chatContentToString(message.content)
					};
				}
				const systemIndex = current.findIndex((message) => message.role === "system");
				const systemMessage = current[systemIndex];
				if (systemMessage?.role === "system") current[systemIndex] = {
					...systemMessage,
					content: next.systemPrompt
				};
				else if (next.systemPrompt) current.unshift({
					role: "system",
					content: next.systemPrompt
				});
				return {
					input: current,
					model: next.model,
					toolChoice: toolsSupported ? input.options?.toolChoice : void 0
				};
			})();
		},
		abortSignal: input.options?.abortSignal,
		temperature: input.options?.temperature,
		topP: input.options?.topP,
		messages,
		headers: mergeRequestHeaders(input.config.headers, input.options?.headers),
		streamOptions: { includeUsage: true },
		stopWhen: stepCountAtLeast(10),
		tools: input.tools,
		toolChoice: input.options?.resolveStep ? void 0 : input.options?.toolChoice,
		onEvent: async (event) => {
			const mapped = toAiriStreamEvent(event);
			if (mapped) await input.onEvent(mapped);
		}
	};
	const result = streamText(requestOptions);
	const generatedTurn = generation.complete(result.messages, result.steps);
	return {
		...result,
		generatedTurn
	};
}
//#endregion
//#region src/runtime/responses.ts
function inputPart(segment) {
	switch (segment.type) {
		case "image": return {
			type: "input_image",
			image_url: segment.url,
			detail: segment.detail
		};
		case "file":
			if (segment.providerFileId) throw new Error("Responses file references require native input Items");
			return {
				type: "input_file",
				file_data: segment.data,
				filename: segment.name,
				file_url: segment.url
			};
		case "audio": throw new Error("This Responses adapter does not support audio input");
		default: return {
			type: "input_text",
			text: renderSegmentText(segment)
		};
	}
}
function renderMessage(message) {
	const items = [];
	const role = message.role === "context" || message.role === "event" || message.role === "summary" ? "user" : message.role;
	let parts = [];
	function flush() {
		if (!parts.length) return;
		if (role === "tool") throw new Error("Tool messages require a correlated tool result");
		if (role === "assistant") {
			const content = parts.map((part) => part.type === "refusal" ? {
				type: "refusal",
				refusal: part.text
			} : {
				type: "output_text",
				text: renderSegmentText(part)
			});
			items.push({
				type: "message",
				role,
				content
			});
		} else if (role === "user") {
			const content = parts.map(inputPart);
			items.push({
				type: "message",
				role,
				content: content.every((part) => part.type === "input_text") ? content.map((part) => part.text).join("") : content
			});
		} else items.push({
			type: "message",
			role,
			content: parts.map((part) => ({
				type: "input_text",
				text: renderSegmentText(part)
			}))
		});
		parts = [];
	}
	for (const segment of message.segments) if (segment.type === "tool-call") {
		if (role !== "assistant") throw new Error("Only assistant messages can invoke tools");
		flush();
		items.push({
			type: "function_call",
			call_id: segment.callId,
			name: segment.name,
			arguments: segment.arguments
		});
	} else if (segment.type === "tool-result") {
		flush();
		const content = segment.content.map(inputPart);
		items.push({
			type: "function_call_output",
			call_id: segment.callId,
			output: content.every((part) => part.type === "input_text") ? content.map((part) => part.text).join("") : content
		});
	} else parts.push(segment);
	flush();
	return items;
}
function renderConversation(conversation, scope) {
	return conversation.turns.flatMap((turn) => {
		if (turn.type !== "assistant") return renderMessage(projectInput(turn));
		return turn.rounds.flatMap((round) => {
			const continuation = round.continuation;
			if (continuation?.protocol === "responses" && continuation.scope === scope) {
				if (!Array.isArray(continuation.data)) throw new Error("Responses continuation must contain an item array");
				return continuation.data;
			}
			return projectRound(round).flatMap(renderMessage);
		});
	});
}
function readToolResultContent(content) {
	if (typeof content === "string") return [{
		type: "text",
		text: content
	}];
	return content.map((part) => {
		switch (part.type) {
			case "input_text": return {
				type: "text",
				text: part.text
			};
			case "input_image":
				if (!part.image_url) throw new Error("Responses image output requires a URL");
				return {
					type: "image",
					url: part.image_url,
					detail: part.detail ?? void 0
				};
			case "input_file":
				if (part.file_data != null && part.file_url == null) return {
					type: "file",
					data: part.file_data,
					name: part.filename ?? void 0
				};
				if (part.file_url != null && part.file_data == null) return {
					type: "file",
					url: part.file_url,
					name: part.filename ?? void 0
				};
				throw new Error("Responses file requires exactly one source");
			case "input_video": throw new Error("Video tool output is not supported by the conversation model");
		}
		throw new Error("Unsupported Responses tool output");
	});
}
function readCitations(part) {
	return part.annotations?.map((entry) => ({
		url: entry.url,
		title: entry.title,
		startIndex: entry.start_index,
		endIndex: entry.end_index
	}));
}
function readOutput(items) {
	return items.flatMap((item, index) => {
		const id = `output-${index}`;
		if (item.type === "function_call") return [{
			id,
			role: "assistant",
			segments: [{
				type: "tool-call",
				callId: item.call_id,
				name: item.name,
				arguments: item.arguments
			}]
		}];
		if (item.type === "function_call_output") return [{
			id,
			role: "tool",
			segments: [{
				type: "tool-result",
				callId: item.call_id,
				content: readToolResultContent(item.output)
			}]
		}];
		if (item.type === "message" && item.role === "assistant") return [{
			id,
			role: "assistant",
			segments: typeof item.content === "string" ? [{
				type: "text",
				text: item.content
			}] : item.content.map((part) => {
				if (part.type === "output_text") return {
					type: "text",
					text: part.text,
					citations: readCitations(part)
				};
				if (part.type === "refusal") return {
					type: "refusal",
					text: part.refusal
				};
				throw new Error("Unsupported Responses assistant content");
			})
		}];
		if (item.type === "reasoning" || item.type === "compaction" || item.type === "web_search_call") return [];
		throw new Error(`Unsupported Responses output item: ${item.type}`);
	});
}
function toolChoice(choice) {
	if (choice == null || typeof choice === "string") return choice;
	if (choice.type === "function") return {
		type: "function",
		name: choice.function.name
	};
	return {
		type: "allowed_tools",
		mode: choice.mode,
		tools: choice.tools.map((tool) => ({
			type: "function",
			name: tool.function.name
		}))
	};
}
/**
* Projects context directly into Responses and runs stateless tool steps.
* Only a fully settled generation produces a generated turn for the caller to commit.
*/
function streamResponses(input) {
	const items = renderConversation(input.conversation, input.scope);
	const scopes = [];
	const generation = createGeneration({
		turnId: input.options?.requestCorrelation?.turnId ?? input.options?.generationTurnId,
		runId: input.options?.requestCorrelation?.runId,
		model: input.config.model,
		roundOffset: input.options?.generationRoundOffset,
		continuation: (data, index) => ({
			protocol: "responses",
			scope: scopes[index] ?? input.scope,
			data
		}),
		project: (item) => readOutput([item])
	});
	let providerConfigKeys = Object.keys(input.config);
	const requestOptions = {
		...input.config,
		prepareStep: ({ input: current, steps }) => {
			const resolveStep = input.options?.resolveStep;
			if (!resolveStep) {
				scopes.push(input.scope);
				return generation.prepareStep({ input: current });
			}
			return (async () => {
				const firstStep = scopes.length === 0 && input.initialStep;
				const next = firstStep || await resolveStep();
				const nextRequest = firstStep ? {
					protocol: "responses",
					config: input.config,
					webSearch: input.webSearch ?? false
				} : next.chatProvider.generation(next.model);
				const nextScope = createContinuationScope(nextRequest.config, {
					...input.options,
					providerId: next.providerId
				});
				if (nextRequest.protocol !== "responses" || nextScope !== input.scope) throw new RequestSwitch(next, await generation.complete(Promise.resolve(current), Promise.resolve(steps)));
				const toolsSupported = supportsTools(next.model, nextRequest, input.options);
				providerConfigKeys = replaceProviderConfig(requestOptions, providerConfigKeys, nextRequest.config);
				Object.assign(requestOptions, {
					apiKey: nextRequest.config.apiKey,
					fetch: nextRequest.config.fetch,
					reasoning: nextRequest.config.reasoning,
					temperature: next.temperature,
					topP: next.topP,
					headers: mergeRequestHeaders(nextRequest.config.headers, next.headers),
					tools: toolsSupported ? nextRequest.webSearch ? [...next.tools ?? [], { type: "web_search" }] : next.tools : void 0,
					toolChoice: void 0
				});
				generation.prepareStep({
					input: current,
					model: next.model
				});
				scopes.push(nextScope);
				const systemIndex = current.findIndex((item) => item.type === "message" && item.role === "system");
				const systemMessage = current[systemIndex];
				if (systemMessage?.type === "message" && systemMessage.role === "system") current[systemIndex] = {
					...systemMessage,
					content: next.systemPrompt
				};
				else if (next.systemPrompt) current.unshift({
					type: "message",
					role: "system",
					content: next.systemPrompt
				});
				return {
					input: current,
					model: next.model,
					toolChoice: toolsSupported ? toolChoice(input.options?.toolChoice) : void 0
				};
			})();
		},
		input: items,
		store: false,
		include: ["reasoning.encrypted_content"],
		abortSignal: input.options?.abortSignal,
		temperature: input.options?.temperature,
		topP: input.options?.topP,
		headers: mergeRequestHeaders(input.config.headers, input.options?.headers),
		tools: input.webSearch ? [...input.tools ?? [], { type: "web_search" }] : input.tools,
		toolChoice: input.options?.resolveStep ? void 0 : toolChoice(input.options?.toolChoice),
		stopWhen: stepCountAtLeast(10),
		onEvent: async (event) => {
			const mapped = toAiriStreamEvent(event);
			if (mapped) await input.onEvent(mapped);
		},
		onNativeEvent: async (event) => {
			if (event.type !== "response.output_item.done" && event.type !== "response.output_item.added") return;
			const item = event.item;
			if (item?.type === "web_search_call") await input.onEvent({
				type: "search",
				id: item.id,
				status: item.status
			});
			if (event.type === "response.output_item.done" && item?.type === "message" && item.role === "assistant") for (const part of item.content) {
				if (part.type !== "output_text") continue;
				const citations = readCitations(part);
				if (citations?.length) await input.onEvent({
					type: "citations",
					citations
				});
			}
		}
	};
	const result = responses(requestOptions);
	const generatedTurn = generation.complete(result.input, result.steps);
	return {
		...result,
		generatedTurn
	};
}
//#endregion
//#region src/runtime/llm-service.ts
/**
* Automatic retry for provider requests that fail with a temporary HTTP status
* before any stream event reaches the consumer.
*/
const transientRetry = {
	/** Wait before each retry when the provider sends no usable `Retry-After`; its length caps the retry count. */
	backoffMs: [
		3e3,
		6e3,
		12e3
	],
	/** Longest server-requested `Retry-After` to wait silently; a longer one fails the turn so the user decides. */
	maxRetryAfterMs: 3e4
};
/** Returns the wait before the next attempt, or `undefined` when the failure is permanent or retries are spent. */
function transientRetryDelayMs(error, attempt) {
	if (attempt >= transientRetry.backoffMs.length || !(error instanceof APICallError)) return void 0;
	if (error.statusCode !== 408 && error.statusCode !== 429 && error.statusCode < 500) return void 0;
	const retryAfter = error.responseHeaders["retry-after"];
	if (!retryAfter) return transientRetry.backoffMs[attempt];
	const seconds = Number(retryAfter);
	const delayMs = Number.isFinite(seconds) ? seconds * 1e3 : Date.parse(retryAfter) - Date.now();
	if (Number.isNaN(delayMs)) return transientRetry.backoffMs[attempt];
	return delayMs <= transientRetry.maxRetryAfterMs ? Math.max(delayMs, 0) : void 0;
}
function waitBeforeRetry(delayMs, signal) {
	return new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(signal.reason);
			return;
		}
		const timer = setTimeout(() => {
			signal?.removeEventListener("abort", onAbort);
			resolve();
		}, delayMs);
		signal?.addEventListener("abort", onAbort, { once: true });
		function onAbort() {
			clearTimeout(timer);
			reject(signal?.reason);
		}
	});
}
async function resolveTools(options) {
	return (typeof options?.tools === "function" ? await options.tools() : options?.tools) ?? [];
}
/** Runs the selected protocol adapter and waits for its generated turn and event consumers. */
async function streamOnce({ model, chatProvider, conversation, options, builtinToolsResolver }) {
	const initialStep = await options?.resolveStep?.();
	const currentModel = initialStep?.model ?? model;
	const request = (initialStep?.chatProvider ?? chatProvider).generation(currentModel);
	const supportedTools = supportsTools(currentModel, request, options);
	const contentArraySupported = supportsContentArray(currentModel, request, options);
	if (request.protocol === "chat-completions" && !contentArraySupported && options?.prepareStringContent) conversation = await options.prepareStringContent(conversation);
	options?.abortSignal?.throwIfAborted();
	const builtinTools = supportedTools && !initialStep ? await (builtinToolsResolver?.(model, chatProvider) ?? Promise.resolve([])) : [];
	const customTools = supportedTools ? initialStep ? initialStep.tools ?? [] : await resolveTools(options) : [];
	const mergedTools = supportedTools ? [...builtinTools, ...customTools] : [];
	const tools = mergedTools.length > 0 ? mergedTools : void 0;
	const scope = createContinuationScope(request.config, {
		...options,
		providerId: initialStep?.providerId ?? options?.providerId
	});
	return new Promise((resolve, reject) => {
		let settled = false;
		let stepsSettled = false;
		const resolveOnce = () => {
			if (settled) return;
			settled = true;
			resolve();
		};
		const rejectOnce = (error) => {
			if (settled || stepsSettled) return;
			settled = true;
			reject(error);
		};
		const onEvent = async (streamEvent) => {
			try {
				if (streamEvent != null) await options?.onStreamEvent?.(streamEvent);
				if (streamEvent?.type === "error") rejectOnce(streamEvent.error);
			} catch (error) {
				rejectOnce(error);
				if (request.protocol === "responses") throw error;
			}
		};
		try {
			const streamResult = request.protocol === "responses" ? streamResponses({
				config: request.config,
				webSearch: supportedTools && request.webSearch,
				conversation,
				scope,
				options,
				tools,
				initialStep,
				onEvent
			}) : streamChatCompletions({
				config: request.config,
				conversation,
				scope,
				options,
				tools,
				initialStep,
				onEvent,
				supportsContentArray: contentArraySupported
			});
			streamResult.steps.then(async () => {
				if (settled) return;
				stepsSettled = true;
				try {
					const generatedTurn = await streamResult.generatedTurn;
					await options?.onStreamEvent?.({ type: "finish" });
					if (options?.abortSignal?.aborted) throw options.abortSignal.reason;
					await options?.onGeneratedTurn?.(generatedTurn);
				} catch (error) {
					if (!settled) {
						settled = true;
						reject(error);
					}
					return;
				}
				let usage;
				try {
					usage = await streamResult.totalUsage;
				} catch (error) {
					console.error("Stream totalUsage error:", error);
				}
				try {
					const normalizedUsage = !usage || usage.inputTokens == null && usage.outputTokens == null && usage.totalTokens == null ? { source: "unavailable" } : {
						...usage,
						source: "reported"
					};
					await options?.onUsage?.(normalizedUsage);
				} catch (error) {
					console.error("Stream usage callback error:", error);
				}
				resolveOnce();
			}).catch((error) => {
				if (stepsSettled) {
					console.error("Stream usage observation error:", error);
					resolveOnce();
					return;
				}
				rejectOnce(error);
				if (!(error instanceof RequestSwitch)) console.error("Stream steps error:", error);
			});
			streamResult.generatedTurn.catch((error) => {
				if (!(error instanceof RequestSwitch)) console.error("Stream generated turn error:", error);
			});
			streamResult.usage.catch((error) => {
				if (!(error instanceof RequestSwitch)) console.error("Stream usage error:", error);
			});
			streamResult.totalUsage.catch((error) => {
				if (!(error instanceof RequestSwitch)) console.error("Stream totalUsage error:", error);
			});
		} catch (error) {
			rejectOnce(error);
		}
	});
}
/**
* Runs one request and repeats it while the provider reports temporary unavailability.
*
* Retrying is only safe while the consumer has seen no stream event. After the
* first event, text may already be rendered or spoken and tools may already have
* run, so a repeat would duplicate them; those failures surface for manual Retry.
* Cancelling through `abortSignal` also ends a pending retry wait.
*/
async function streamWithTransientRetry(input) {
	for (let attempt = 0;; attempt++) {
		let consumerNotified = false;
		try {
			return await streamOnce({
				...input,
				options: {
					...input.options,
					onStreamEvent: (event) => {
						consumerNotified = true;
						return input.options?.onStreamEvent?.(event);
					}
				}
			});
		} catch (error) {
			const delayMs = consumerNotified ? void 0 : transientRetryDelayMs(error, attempt);
			if (delayMs == null) throw error;
			console.warn(`[llm] Retrying provider request in ${delayMs}ms (retry ${attempt + 1}/${transientRetry.backoffMs.length}):`, error);
			await waitBeforeRetry(delayMs, input.options?.abortSignal);
		}
	}
}
function mergeGenerationUsage(rounds, last) {
	const partial = rounds.flatMap((round) => round.modelCall?.usage ? [round.modelCall.usage] : []);
	if (partial.length === 0) return last;
	return {
		inputTokens: partial.reduce((sum, usage) => sum + usage.inputTokens, last?.inputTokens ?? 0),
		outputTokens: partial.reduce((sum, usage) => sum + usage.outputTokens, last?.outputTokens ?? 0),
		totalTokens: partial.reduce((sum, usage) => sum + usage.totalTokens, last?.totalTokens ?? 0),
		source: "reported"
	};
}
/** Keeps one assistant turn across xsAI tool loops when the next request changes provider scope. */
async function streamFrom(input) {
	if (!input.options?.resolveStep) return streamWithTransientRetry(input);
	const completedRounds = [];
	let turnId = input.options.requestCorrelation?.turnId;
	let request = input;
	let switches = 0;
	while (true) {
		let finalTurn;
		let lastUsage;
		try {
			await streamWithTransientRetry({
				...request,
				options: {
					...request.options,
					generationTurnId: turnId,
					generationRoundOffset: completedRounds.length,
					onGeneratedTurn: (turn) => {
						finalTurn = turn;
					},
					onUsage: (usage) => {
						lastUsage = usage;
					}
				}
			});
			if (finalTurn) await input.options.onGeneratedTurn?.({
				...finalTurn,
				rounds: [...completedRounds, ...finalTurn.rounds]
			});
			const usage = mergeGenerationUsage(completedRounds, lastUsage);
			if (usage) await input.options.onUsage?.(usage);
			return;
		} catch (error) {
			if (!(error instanceof RequestSwitch)) throw error;
			switches += 1;
			if (switches > 10) throw new Error("Generation request scope changed too many times");
			turnId = error.partialTurn.id;
			completedRounds.push(...error.partialTurn.rounds);
			if (completedRounds.length >= 10) throw new Error("Generation tool step limit reached");
			request = {
				...input,
				model: error.next.model,
				chatProvider: error.next.chatProvider,
				options: {
					...input.options,
					providerId: error.next.providerId,
					headers: error.next.headers
				},
				conversation: { turns: [...input.conversation.turns, {
					...error.partialTurn,
					rounds: [...completedRounds]
				}] }
			};
		}
	}
}
const TOOLS_RELATED_ERROR_PATTERNS = [
	/does not support tools/i,
	/no endpoints found that support tool use/i,
	/functions are not supported/i,
	/unrecognized request argument.+tools/i,
	/tool use with function calling is unsupported/i,
	/does not support function.?calling/i,
	/tools?\s+(is|are)\s+not\s+supported/i
];
function isToolRelatedError(error) {
	const message = String(error);
	return TOOLS_RELATED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}
const CONTENT_ARRAY_RELATED_ERROR_PATTERNS = [/messages\[\d+\][^"]*invalid type:\s*sequence,\s*expected\s+a\s+string/i, /messages\.\d+\.content[^"]*(?:expected|should be).*string/i];
/**
* Whether the given error indicates the provider rejected content-part arrays
* and the caller should auto-degrade to string-only `content` for this model.
*
* Use when:
* - Catching errors thrown by {@link streamFrom} so the chat store can flip
*   `contentArrayCompatibility` for the failing model key.
*
* Expects:
* - `error` may be an Error instance, a thrown SDK response object, a string,
*   or anything else; we coerce via `String(error)` and pattern-match.
*
* Returns:
* - `true` when the message matches a known "content array unsupported" wire
*   format from an OpenAI-compatible gateway, otherwise `false`.
*/
function isContentArrayRelatedError(error) {
	const message = String(error);
	return CONTENT_ARRAY_RELATED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}
//#endregion
//#region src/session/merge-loaded-session-messages.ts
function extractMessageContent(message) {
	if (typeof message.content === "string") return message.content;
	if (Array.isArray(message.content)) return message.content.map((part) => {
		if (typeof part === "string") return part;
		if (part && typeof part === "object" && "text" in part) return String(part.text ?? "");
		return "";
	}).join("");
	return "";
}
function getMessageFingerprint(message) {
	return [
		message.id ?? "",
		message.role,
		message.createdAt ?? "",
		extractMessageContent(message)
	].join("");
}
function mergeLoadedSessionMessages(storedMessages, currentMessages) {
	if (currentMessages.length === 0) return storedMessages;
	const currentNonSystemMessages = currentMessages.filter((message, index) => index !== 0 || message.role !== "system");
	if (currentNonSystemMessages.length === 0) return storedMessages;
	const seen = new Set(storedMessages.map(getMessageFingerprint));
	const extraMessages = currentNonSystemMessages.filter((message) => {
		const fingerprint = getMessageFingerprint(message);
		if (seen.has(fingerprint)) return false;
		seen.add(fingerprint);
		return true;
	});
	if (extraMessages.length === 0) return storedMessages;
	const systemMessage = storedMessages[0]?.role === "system" ? storedMessages[0] : currentMessages[0]?.role === "system" ? currentMessages[0] : void 0;
	if (storedMessages.length === 0 && systemMessage) return [systemMessage, ...extraMessages];
	return [...storedMessages, ...extraMessages];
}
//#endregion
//#region src/utils/error.ts
/** Returns a message for runtime diagnostics, including values thrown without an Error wrapper. */
function errorMessageFromValue(error) {
	return errorMessageFrom(error) ?? String(error);
}
/**
* Preserves an Error thrown by a provider and wraps other thrown values with operation context.
*
* @example
* errorFromCause('offline', 'Speech synthesis failed')
* // => Error('Speech synthesis failed', { cause: 'offline' })
*/
function errorFromCause(cause, message) {
	return cause instanceof Error ? cause : new Error(message, { cause });
}
//#endregion
//#region src/voice/input/transcript.ts
/** Owns provider revisions and raw history. A completed transcript cannot reopen. */
var Transcript = class {
	current = Object.freeze({
		revision: 0,
		text: "",
		segments: Object.freeze([])
	});
	updates = [];
	complete = false;
	revision = 0;
	patches = [];
	get raw() {
		return this.current;
	}
	get history() {
		return this.updates.slice();
	}
	get ended() {
		return this.complete;
	}
	get corrected() {
		return this.select("corrected");
	}
	get patchHistory() {
		return this.patches.map((patch) => Object.freeze({
			...patch.record,
			active: patch.active
		}));
	}
	select(view, authors) {
		if (view === "raw") return this.raw;
		const patches = this.patches.filter((patch) => patch.active && (patch.record.manual || !authors || authors.includes(patch.record.author)));
		const segments = this.raw.segments.map((segment) => {
			const spans = patches.flatMap((patch) => patch.spans.filter((span) => span.segmentId === segment.id)).sort((a, b) => b.start - a.start);
			let text = segment.text;
			for (const span of spans) text = text.slice(0, span.start) + span.replacement + text.slice(span.end);
			return Object.freeze({
				...segment,
				text
			});
		});
		return Object.freeze({
			revision: this.revision,
			text: segments.map((segment) => segment.text).join(""),
			segments: Object.freeze(segments)
		});
	}
	/** Selected patch identities preserve freshness even when a correction restores an earlier text value. */
	patchVersions(segmentIds, authors) {
		return this.patches.filter((patch) => patch.active && (patch.record.manual || authors.includes(patch.record.author)) && patch.spans.some((span) => segmentIds.includes(span.segmentId))).map((patch) => patch.record.id);
	}
	patch(author, proposal, current, manual = false) {
		if (!current()) return {
			status: "rejected",
			reason: "stale"
		};
		const spans = [];
		for (const edit of proposal.edits) {
			if (edit.range.kind === "tokens" && (!Number.isSafeInteger(edit.range.start) || !Number.isSafeInteger(edit.range.end) || edit.range.start < 0 || edit.range.end <= edit.range.start)) return {
				status: "rejected",
				reason: "conflict"
			};
			const segment = this.raw.segments.find((segment) => segment.id === edit.segmentId);
			if (!segment) return {
				status: "rejected",
				reason: "stale"
			};
			const start = edit.range.kind === "segment" ? 0 : segment.tokens[edit.range.start]?.start;
			const end = edit.range.kind === "segment" ? segment.text.length : segment.tokens[edit.range.end - 1]?.end;
			if (start === void 0 || end === void 0 || end < start || segment.text.slice(start, end) !== edit.expectedText) return {
				status: "rejected",
				reason: "conflict"
			};
			const overlaps = (span) => span.segmentId === segment.id && (start < span.end && end > span.start || start === end && start === span.start);
			if (spans.some(overlaps) || this.patches.some((patch) => patch.active && patch.spans.some(overlaps))) return {
				status: "rejected",
				reason: "conflict"
			};
			spans.push({
				segmentId: segment.id,
				revision: segment.revision,
				start,
				end,
				expectedText: edit.expectedText,
				replacement: edit.replacement
			});
		}
		if (!spans.length) return {
			status: "rejected",
			reason: "conflict"
		};
		this.patches.push({
			record: Object.freeze({
				id: nanoid$1(),
				author,
				manual,
				edits: Object.freeze(proposal.edits.map((edit) => Object.freeze({
					...edit,
					range: Object.freeze({ ...edit.range })
				}))),
				evidenceIds: Object.freeze([...proposal.evidenceIds])
			}),
			spans,
			current,
			active: true
		});
		this.revision += 1;
		return { status: "applied" };
	}
	invalidate() {
		let changed = false;
		for (const patch of this.patches) {
			const targetChanged = patch.spans.some((span) => {
				const segment = this.raw.segments.find((segment) => segment.id === span.segmentId);
				if (!segment) return true;
				return patch.record.manual ? segment.text.slice(span.start, span.end) !== span.expectedText : segment.revision !== span.revision;
			});
			if (patch.active && (!patch.current() || targetChanged)) {
				patch.active = false;
				changed = true;
			}
		}
		if (changed) this.revision += 1;
		return changed;
	}
	removeAuthor(author) {
		for (const patch of this.patches) if (patch.active && !patch.record.manual && patch.record.author === author) {
			patch.active = false;
			this.revision += 1;
		}
	}
	/** Triggering workflow: provider output → SpeechInputAttempt → raw document update or completion. */
	accept(event) {
		if (this.complete) throw new Error("Transcription emitted after completion");
		if (event.type === "complete") {
			if (event.revision !== this.current.revision || this.current.segments.some((segment) => !segment.final)) throw new Error("Transcription completion does not match final text");
			this.complete = true;
			return;
		}
		if (!Number.isSafeInteger(event.revision) || event.revision <= this.current.revision) throw new Error("Transcription revisions must increase");
		const ids = /* @__PURE__ */ new Set();
		const segments = event.segments.map((segment) => {
			if (ids.has(segment.id) || !segment.id || !Number.isSafeInteger(segment.revision) || segment.revision < 0) throw new Error("Invalid transcription segment identity");
			ids.add(segment.id);
			const previous = this.current.segments.find((item) => item.id === segment.id);
			const changedTokens = previous && (segment.tokens.length !== previous.tokens.length || segment.tokens.some((token, index) => {
				const old = previous.tokens[index];
				return token.text !== old?.text || token.start !== old?.start || token.end !== old?.end;
			}));
			if (previous && (segment.revision < previous.revision || segment.revision === previous.revision && (segment.text !== previous.text || segment.final !== previous.final || changedTokens))) throw new Error("Changed segment requires a new revision");
			let end = 0;
			for (const token of segment.tokens) {
				if (!Number.isSafeInteger(token.start) || !Number.isSafeInteger(token.end) || token.start < end || token.end <= token.start || token.end > segment.text.length || segment.text.slice(token.start, token.end) !== token.text) throw new Error("Invalid transcription token range");
				end = token.end;
			}
			return Object.freeze({
				...segment,
				tokens: Object.freeze(segment.tokens.map((token) => Object.freeze({ ...token })))
			});
		});
		this.current = Object.freeze({
			revision: event.revision,
			text: segments.map((segment) => segment.text).join(""),
			segments: Object.freeze(segments)
		});
		this.updates.push(this.current);
		this.revision += 1;
	}
};
//#endregion
//#region src/voice/input/speech-input.ts
/** Accepted input identity stays with its character session until submission or cancellation. */
var SpeechInput = class {
	id;
	sessionId;
	document = new Transcript();
	get transcript() {
		return this.document;
	}
	entries = /* @__PURE__ */ new Map();
	versions = /* @__PURE__ */ new Map();
	listeners = /* @__PURE__ */ new Set();
	sealed = false;
	speakersFrozen = false;
	snapshotVersions = /* @__PURE__ */ new WeakMap();
	speaker;
	constructor(id, sessionId) {
		this.id = id;
		this.sessionId = sessionId;
	}
	get closed() {
		return this.sealed;
	}
	get speakers() {
		return this.speaker;
	}
	accept(event) {
		if (this.sealed) throw new Error("Speech input is closed");
		this.document.accept(event);
		this.changed();
	}
	patch(...args) {
		if (this.sealed) return {
			status: "rejected",
			reason: "closed"
		};
		const result = this.document.patch(...args);
		if (result.status === "applied") this.changed();
		return result;
	}
	finishTranscription() {
		if (!this.document.ended) throw new Error("Provider closed without transcription completion");
		this.speakersFrozen = true;
	}
	updateSpeakers(evidence) {
		if (this.sealed || this.speakersFrozen) return {
			status: "rejected",
			reason: "closed"
		};
		if (!Number.isFinite(evidence.voicedMs) || evidence.voicedMs < 0 || evidence.candidates.some((candidate) => !candidate.speakerId || !Number.isFinite(candidate.score))) throw new Error("Invalid speaker evidence");
		this.speaker = Object.freeze({
			...evidence,
			revision: (this.speaker?.revision ?? 0) + 1,
			candidates: Object.freeze(evidence.candidates.map((candidate) => Object.freeze({ ...candidate })))
		});
		this.changed();
		return { status: "applied" };
	}
	get context() {
		return [...this.entries.values()].map(({ current: _current, ...entry }) => Object.freeze(entry));
	}
	snapshot(selection, correctionPlugins, targetSegmentId) {
		const document = this.transcript.select(selection.transcript, correctionPlugins);
		const index = document.segments.findIndex((segment) => segment.id === targetSegmentId);
		const target = document.segments[index];
		const segmentScope = typeof selection.scope === "object" ? selection.scope : void 0;
		const transcript = segmentScope ? {
			...document,
			text: target?.text ?? "",
			segments: target ? [target] : []
		} : document;
		const neighborView = segmentScope?.neighborTranscript ?? selection.transcript;
		const neighborsDocument = this.transcript.select(neighborView, correctionPlugins);
		const neighbors = segmentScope && target ? neighborsDocument.segments.slice(Math.max(0, index - segmentScope.neighbors), index + segmentScope.neighbors + 1).filter((segment) => segment.id !== target.id).map((segment) => ({
			view: neighborView,
			segment
		})) : [];
		const snapshot = Object.freeze({
			inputId: this.id,
			sessionId: this.sessionId,
			transcript: Object.freeze({
				...transcript,
				view: selection.transcript,
				targetSegmentId
			}),
			neighbors: Object.freeze(neighbors),
			correctionPlugins,
			...selection.speakers ? { speakers: this.speaker } : {},
			context: Object.freeze((selection.context ?? []).map((selector) => {
				const segmentId = selector.scope === "target-segment" ? targetSegmentId : void 0;
				const identity = JSON.stringify([
					selector.plugin,
					selector.key,
					segmentId
				]);
				const entry = this.entries.get(identity);
				return Object.freeze({
					plugin: selector.plugin,
					key: selector.key,
					...segmentId ? { segmentId } : {},
					revision: this.versions.get(identity) ?? 0,
					...entry ? { value: entry.value } : {}
				});
			}))
		});
		const selectedIds = snapshot.transcript.segments.map((segment) => segment.id);
		const neighborIds = neighbors.map((neighbor) => neighbor.segment.id);
		this.snapshotVersions.set(snapshot, JSON.stringify([
			snapshot.transcript.segments,
			neighbors,
			segmentScope ? [index < segmentScope.neighbors, document.segments.length - index - 1 < segmentScope.neighbors] : this.transcript.raw.revision,
			selection.transcript === "corrected" ? this.transcript.patchVersions(selectedIds, correctionPlugins) : [],
			neighborView === "corrected" ? this.transcript.patchVersions(neighborIds, correctionPlugins) : [],
			snapshot.context.map((entry) => [
				entry.plugin,
				entry.key,
				entry.segmentId,
				entry.revision
			]),
			snapshot.speakers?.revision
		]));
		return snapshot;
	}
	/** Revisions, including absent context keys, are compared without serializing application values. */
	basis(snapshot) {
		return this.snapshotVersions.get(snapshot);
	}
	publish(plugin, key, value, current, segmentId) {
		if (this.sealed) return {
			status: "rejected",
			reason: "closed"
		};
		if (!current()) return {
			status: "rejected",
			reason: "stale"
		};
		const identity = JSON.stringify([
			plugin,
			key,
			segmentId
		]);
		const revision = (this.versions.get(identity) ?? 0) + 1;
		this.versions.set(identity, revision);
		this.entries.set(identity, {
			plugin,
			key,
			...segmentId ? { segmentId } : {},
			value,
			revision,
			current
		});
		this.changed();
		return { status: "applied" };
	}
	deleteContext(plugin, key, current, segmentId) {
		if (this.sealed) return {
			status: "rejected",
			reason: "closed"
		};
		if (!current()) return {
			status: "rejected",
			reason: "stale"
		};
		const identity = JSON.stringify([
			plugin,
			key,
			segmentId
		]);
		this.entries.delete(identity);
		this.versions.set(identity, (this.versions.get(identity) ?? 0) + 1);
		this.changed();
		return { status: "applied" };
	}
	subscribe(listener) {
		this.listeners.add(listener);
		return () => this.listeners.delete(listener);
	}
	/** Triggering workflow: transcript or context publication → dependency invalidation → subscribed plugin snapshots. */
	changed() {
		let removed = false;
		do {
			removed = this.document.invalidate();
			for (const [identity, entry] of this.entries) if (!entry.current()) {
				this.entries.delete(identity);
				this.versions.set(identity, (this.versions.get(identity) ?? 0) + 1);
				removed = true;
			}
		} while (removed);
		for (const listener of this.listeners) listener();
	}
	removePlugin(plugin) {
		if (this.sealed) return;
		this.document.removeAuthor(plugin);
		for (const [identity, entry] of this.entries) if (entry.plugin === plugin) {
			this.entries.delete(identity);
			this.versions.set(identity, entry.revision + 1);
		}
		this.changed();
	}
	seal() {
		this.sealed = true;
		this.listeners.clear();
	}
};
//#endregion
//#region src/voice/input/attempt.ts
/** Owns one admission, capture, provider request, and submission. Normal end never aborts the provider. */
var SpeechInputAttempt = class {
	owner;
	options;
	id = nanoid$1();
	sessionId;
	done;
	completion = Promise.withResolvers();
	abort = new AbortController();
	listeners = /* @__PURE__ */ new Set();
	capture;
	reader;
	current = {
		phase: "pending",
		waitingFor: "source"
	};
	accepted;
	plugins;
	audio;
	activityEnd = 0;
	speechRevision = 0;
	from;
	sealedFrame;
	detector;
	endProposal;
	stage = "source";
	started = false;
	constructor(owner, options) {
		this.owner = owner;
		this.options = options;
		this.sessionId = options.sessionId;
		this.done = this.completion.promise;
	}
	get state() {
		return this.current;
	}
	get input() {
		return this.accepted;
	}
	subscribe(listener) {
		this.listeners.add(listener);
		this.notify(listener);
		return () => this.listeners.delete(listener);
	}
	end() {
		if (this.current.phase === "pending") this.cancel("Input ended before capture started");
		if (this.current.phase === "capturing") {
			this.owner.seal(this);
			this.sealedFrame = this.audio?.position?.frame;
			this.change({ phase: "finalizing" });
			this.detector?.cancel("Speech input is finalizing");
			this.capture?.finish();
		}
		return this.done;
	}
	cancel(reason) {
		this.settle({
			status: "cancelled",
			reason
		});
	}
	noteActivity(evidence) {
		const range = evidence.range;
		const acceptsPending = this.current.phase === "pending" && this.capture !== void 0;
		if (this.current.phase !== "capturing" && !acceptsPending) return false;
		if (!this.acceptsEvidence(range) || !Number.isSafeInteger(range.startFrame) || !Number.isSafeInteger(range.endFrame)) return false;
		if (range.endFrame <= this.activityEnd) return false;
		if (range.startFrame > this.activityEnd) {
			this.endProposal = void 0;
			return false;
		}
		this.activityEnd = range.endFrame;
		if (evidence.speech) this.speechRevision += 1;
		this.endProposal?.();
		return true;
	}
	acceptsEvidence(range) {
		if (!this.from || range.sourceId !== this.from.sourceId) return false;
		const latestFrame = this.sealedFrame ?? this.audio?.position?.frame ?? 0;
		return range.startFrame >= this.from.frame && range.endFrame > range.startFrame && range.endFrame <= latestFrame;
	}
	detectEnd(options, detector) {
		if (!Number.isFinite(options.windowMs) || options.windowMs <= 0 || !Number.isFinite(options.hopMs) || options.hopMs <= 0) throw new Error("Audio window and hop must be finite and positive");
		this.detector?.cancel("Replaced by a new end detector");
		const completion = Promise.withResolvers();
		let observer;
		let closed = false;
		const observationHandle = {
			done: completion.promise,
			cancel: (reason) => {
				if (closed) return;
				closed = true;
				observer?.cancel(reason);
				if (this.detector === observationHandle) {
					this.detector = void 0;
					this.endProposal = void 0;
				}
				completion.resolve({
					status: "cancelled",
					reason
				});
			},
			start: () => {
				if (closed || observer || !this.audio || !this.accepted || this.current.phase !== "capturing") return;
				observer = observe(this.audio, options, async (window, signal) => {
					const transcript = this.accepted.transcript.raw;
					const speechRevision = this.speechRevision;
					const context = this.owner.options.conversationContext?.(this.sessionId) ?? {
						revision: 0,
						messages: []
					};
					return {
						result: await detector({
							audio: window,
							transcript,
							speechRevision,
							context
						}, signal),
						transcript,
						speechRevision,
						context
					};
				}, (observation) => {
					if (closed || observation.value.result !== "end") return;
					const { transcript, speechRevision, context } = observation.value;
					const apply = () => {
						if (closed || this.current.phase !== "capturing") return;
						const currentContextRevision = this.owner.options.conversationContext?.(this.sessionId).revision ?? 0;
						if (transcript.revision !== this.accepted.transcript.raw.revision || speechRevision !== this.speechRevision || context.revision !== currentContextRevision) {
							this.endProposal = void 0;
							return;
						}
						if (options.activity === "ordered" && observation.range.endFrame > this.activityEnd) return;
						this.end();
					};
					this.endProposal = apply;
					apply();
				});
				observer.done.then((outcome) => {
					if (closed) return;
					completion.resolve(outcome);
					observationHandle.cancel("End detection ended");
					if (outcome.status === "failed") this.settle({
						status: "failed",
						stage: "detector",
						error: outcome.error
					});
				});
			}
		};
		this.detector = observationHandle;
		if (this.current.phase === "finalizing" || this.current.phase === "settled") observationHandle.cancel("Speech input is no longer capturing");
		else observationHandle.start();
		return observationHandle;
	}
	/** Triggering workflow: VoiceController.beginInput → admission → capture and provider output → submit. */
	async start() {
		if (this.started) throw new Error("Speech input attempt already started");
		this.started = true;
		try {
			const prepared = await this.admitCapture();
			if (!prepared) return;
			const { provider, capture } = prepared;
			this.accepted = new SpeechInput(this.id, this.sessionId);
			this.plugins = this.owner.createPlugins(this.accepted);
			this.change({
				phase: "capturing",
				from: this.from
			});
			this.detector?.start();
			if (!await this.readTranscription(provider, capture)) return;
			await this.submitInput();
		} catch (cause) {
			this.settle({
				status: "failed",
				stage: this.stage,
				error: errorFromCause(cause, "Speech input failed")
			});
		} finally {
			this.reader?.releaseLock();
		}
	}
	/**
	* Speech onset reserves its capture before playback silence, so retained pre-roll survives the fade.
	* A manual input starts capture after silence, so the assistant's last audio stays out of the transcript.
	* Admission ends when the source delivers audio, which also covers a pending microphone permission.
	*/
	async admitCapture() {
		const audio = this.owner.audio();
		if (!audio) throw new Error("Audio input is not connected");
		let reserved = this.options.start.kind === "speech-onset" ? this.prepareCapture(audio) : void 0;
		const interruption = this.options.interruptTurns.length ? this.owner.interrupt({
			turns: this.options.interruptTurns,
			cause: "speech-input"
		}) : void 0;
		if (interruption) {
			this.change({
				phase: "pending",
				waitingFor: "silence"
			});
			this.stage = "admission";
			const failed = (await interruption.silenced).find((receipt) => receipt.status === "failed");
			if (failed) throw failed.error ?? /* @__PURE__ */ new Error("Playback silence was not confirmed");
		}
		if (this.abort.signal.aborted) return;
		reserved ??= this.prepareCapture(audio);
		this.stage = "source";
		this.change({
			phase: "pending",
			waitingFor: "source"
		});
		await Promise.race([reserved.capture.started, reserved.capture.done]);
		if (this.abort.signal.aborted || this.current.phase === "settled") return;
		this.from ??= audio.position && {
			sourceId: audio.position.sourceId,
			frame: this.activityEnd
		};
		return reserved;
	}
	async readTranscription(provider, capture) {
		this.stage = "transcription";
		this.reader = provider.transcribe({
			audio: capture.stream,
			signal: this.abort.signal
		}).getReader();
		while (!this.abort.signal.aborted) {
			const result = await this.reader.read();
			if (this.abort.signal.aborted) return false;
			if (result.done) break;
			if (result.value.type === "complete" && this.current.phase !== "finalizing") throw new Error("Provider completed before capture ended");
			this.accepted.accept(result.value);
			this.change(this.current);
		}
		this.accepted.finishTranscription();
		return (await capture.done).status === "finished" && !this.abort.signal.aborted;
	}
	async submitInput() {
		this.stage = "submission";
		await this.plugins.finish();
		if (this.abort.signal.aborted) return;
		const accepted = this.accepted;
		const transcript = accepted.transcript;
		const submission = Object.freeze({
			submissionId: this.id,
			sessionId: this.sessionId,
			text: transcript.corrected.text,
			transcript: Object.freeze({
				raw: transcript.raw,
				corrected: transcript.corrected,
				history: transcript.history,
				patches: transcript.patchHistory
			}),
			context: accepted.context,
			...accepted.speakers ? { speakers: accepted.speakers } : {}
		});
		accepted.seal();
		if (!this.owner.options.submit) throw new Error("Speech submission is not configured");
		const outcome = await this.owner.options.submit(submission, this.abort.signal);
		if (!this.abort.signal.aborted) this.settle(outcome);
	}
	prepareCapture(audio) {
		this.stage = "capture";
		this.audio = audio;
		const provider = this.owner.options.transcriber?.(this.sessionId);
		if (!provider) throw new Error("Speech transcription is not configured");
		let from;
		if (this.options.start.kind === "speech-onset") {
			const { at, preRollMs } = this.options.start;
			if (preRollMs && !audio.sampleRate) throw new Error("Source sample rate is unavailable for pre-roll");
			from = preRollMs ? {
				...at,
				frame: Math.max(0, at.frame - Math.floor(preRollMs * audio.sampleRate / 1e3))
			} : at;
			this.activityEnd = at.frame;
		} else this.activityEnd = audio.position?.frame ?? 0;
		this.from = from ?? audio.position;
		const recording = capture(audio, {
			from,
			signal: this.abort.signal
		});
		this.capture = recording;
		recording.done.then((outcome) => {
			if (outcome.status === "failed") this.settle({
				status: "failed",
				stage: "capture",
				error: outcome.error
			});
			if (outcome.status === "cancelled") this.cancel(outcome.reason);
			if (outcome.status === "finished" && this.current.phase === "capturing") this.end();
		});
		return {
			provider,
			capture: recording
		};
	}
	change(state) {
		this.current = Object.freeze(state);
		for (const listener of this.listeners) this.notify(listener);
	}
	/** Triggering workflow: {@link change} or {@link subscribe} → state snapshot → UI listener and host diagnostics. */
	notify(listener) {
		try {
			listener(this.current);
		} catch (error) {
			this.owner.reportError("subscriber", error);
		}
	}
	settle(outcome) {
		if (this.current.phase === "settled") return;
		this.owner.seal(this);
		this.change({
			phase: "settled",
			outcome
		});
		this.detector?.cancel("Speech input settled");
		if (outcome.status === "cancelled" || outcome.status === "failed") {
			this.abort.abort(outcome);
			this.capture?.cancel("Speech input closed");
			this.reader?.cancel(outcome).catch(() => {});
		}
		this.accepted?.seal();
		this.plugins?.close(outcome.status === "failed" || outcome.status === "cancelled" ? outcome : { status: "finished" });
		this.completion.resolve(outcome);
	}
};
//#endregion
//#region src/voice/output/response.ts
/** One producer owns its text, synthesis, queued audio, and cancellation within a response. */
var SpeechStream = class {
	response;
	purpose;
	output;
	abort = new AbortController();
	completion = Promise.withResolvers();
	intent;
	text;
	outputReader;
	started = false;
	sealed = false;
	settled = false;
	timer;
	signal = this.abort.signal;
	done = this.completion.promise;
	constructor(response, purpose, output) {
		this.response = response;
		this.purpose = purpose;
		this.output = output;
	}
	/** The response registers this stream before it starts synthesis or playback. Setup errors settle `done`. */
	start(previous, deadlineMs) {
		if (this.started) throw new Error("Speech stream already started");
		this.started = true;
		try {
			this.startProvider(previous, deadlineMs);
		} catch (cause) {
			this.fail(errorFromCause(cause, "Speech stream setup failed"));
		}
	}
	startProvider(previous, deadlineMs) {
		if (deadlineMs !== void 0) this.timer = setTimeout(() => this.cancel("Speech deadline ended"), deadlineMs);
		const { output, response } = this;
		if ("stream" in output) {
			this.text = createPushStream();
			this.playStream(previous, output.stream);
			return;
		}
		const listeners = {
			start: /* @__PURE__ */ new Set(),
			end: /* @__PURE__ */ new Set(),
			interrupt: /* @__PURE__ */ new Set()
		};
		const pending = /* @__PURE__ */ new Set();
		const pipeline = createSpeechPipeline({
			tts: async (request, signal) => {
				try {
					const audio = await output.synthesize(request, AbortSignal.any([
						signal,
						this.signal,
						response.signal
					]));
					return this.signal.aborted || response.signal.aborted ? null : audio;
				} catch (cause) {
					this.fail(errorFromCause(cause, "Speech synthesis failed"));
					return null;
				}
			},
			playback: {
				schedule: (item) => {
					pending.add(item);
					const clip = {
						id: item.segmentId,
						text: item.text
					};
					let started = false;
					Promise.race([previous, this.done]).then(async () => {
						if (this.signal.aborted || response.signal.aborted) return "stopped";
						return response.playback.enqueue({
							id: item.id,
							audio: item.audio,
							signal: this.signal,
							onStart: () => {
								started = true;
								listeners.start.forEach((listener) => listener({
									item,
									startedAt: Date.now()
								}));
								output.onPlaybackStart?.(clip);
							}
						});
					}).then((result) => {
						if (started) output.onPlaybackEnd?.(clip, this.clipEnd(result));
						if (!pending.delete(item)) return;
						if (result === "ended") listeners.end.forEach((listener) => listener({
							item,
							endedAt: Date.now()
						}));
						else listeners.interrupt.forEach((listener) => listener({
							item,
							interruptedAt: Date.now(),
							reason: result
						}));
						if (result === "failed") this.fail(/* @__PURE__ */ new Error("Speech playback failed"));
					}, (cause) => this.fail(errorFromCause(cause, "Speech playback failed")));
				},
				stopByIntent: (_id, reason) => {
					for (const item of pending) listeners.interrupt.forEach((listener) => listener({
						item,
						interruptedAt: Date.now(),
						reason
					}));
					pending.clear();
				},
				stopAll: (reason) => this.cancel(reason),
				stopByOwner: (_id, reason) => this.cancel(reason),
				onStart: (listener) => {
					listeners.start.add(listener);
				},
				onEnd: (listener) => {
					listeners.end.add(listener);
				},
				onInterrupt: (listener) => {
					listeners.interrupt.add(listener);
				},
				onReject: () => {}
			}
		});
		pipeline.on("onSpecial", (segment) => segment.special && output.onSpecial?.(segment.special));
		pipeline.on("onIntentEnd", () => this.settle({ status: "finished" }));
		this.intent = pipeline.openIntent({
			turnId: response.turn.turnId,
			ownerId: response.turn.sessionId
		});
	}
	/** Triggering workflow: VoiceController response → provider text stream → reserved playback slot → producer completion. */
	async playStream(previous, synthesize) {
		try {
			const audio = await synthesize(this.text.stream, AbortSignal.any([this.signal, this.response.signal]));
			await Promise.race([previous, this.done]);
			if (this.signal.aborted || this.response.signal.aborted) {
				await audio.cancel("Speech producer closed");
				return;
			}
			this.outputReader = audio.getReader();
			const playing = [];
			while (!this.signal.aborted && !this.response.signal.aborted) {
				const result = await this.outputReader.read();
				if (result.done) break;
				const part = result.value;
				const clip = {
					id: nanoid$1(),
					text: part.text ?? ""
				};
				let started = false;
				playing.push(this.response.playback.enqueue({
					id: clip.id,
					audio: part.audio,
					signal: this.signal,
					onStart: () => {
						started = true;
						this.output.onPlaybackStart?.(clip);
					}
				}).then((status) => {
					if (started) this.output.onPlaybackEnd?.(clip, this.clipEnd(status));
					if (status === "failed") this.fail(/* @__PURE__ */ new Error("Speech playback failed"));
				}));
			}
			if (!this.sealed) throw new Error("Speech provider completed before text input ended");
			await Promise.all(playing);
			this.settle({ status: "finished" });
		} catch (cause) {
			this.fail(errorFromCause(cause, "Streaming speech failed"));
		} finally {
			this.outputReader?.releaseLock();
			this.outputReader = void 0;
		}
	}
	clipEnd(status) {
		if (status === "ended") return { status };
		const signal = this.signal.aborted ? this.signal : this.response.signal;
		return signal.aborted ? {
			status,
			reason: errorMessageFromValue(signal.reason)
		} : { status };
	}
	/** Special tokens remain ordered through the chunker. Streaming providers handle these tokens outside their text transport. */
	special(value) {
		if (this.sealed || this.response.signal.aborted) return;
		if (this.intent) this.intent.writeSpecial(value);
		else this.output.onSpecial?.(value);
	}
	flush() {
		if (!this.sealed) this.intent?.writeFlush();
	}
	write(text) {
		if (this.sealed || this.response.signal.aborted) return { status: "closed" };
		if (this.text) this.text.write(text);
		else this.intent.writeLiteral(text);
		return { status: "accepted" };
	}
	end() {
		if (this.sealed) return;
		this.sealed = true;
		this.text?.close();
		this.intent?.end();
	}
	cancel(reason) {
		if (this.settled) return;
		this.abort.abort(reason);
		this.text?.close();
		this.outputReader?.cancel("Speech producer closed").catch(() => {});
		this.intent?.cancel(reason);
		this.settle({
			status: "cancelled",
			reason
		});
	}
	fail(error) {
		if (this.settled) return;
		this.abort.abort(error);
		this.text?.close();
		this.outputReader?.cancel("Speech producer closed").catch(() => {});
		this.intent?.cancel(error.message);
		this.settle({
			status: "failed",
			error
		});
	}
	settle(outcome) {
		if (this.settled) return;
		this.sealed = true;
		this.settled = true;
		clearTimeout(this.timer);
		this.completion.resolve(outcome);
	}
};
/** Speech producers synthesize concurrently. Their reserved playback order follows creation order. */
var VoiceResponse = class {
	output;
	onClosed;
	abort = new AbortController();
	streams = [];
	ordered = Promise.resolve();
	sealed = false;
	outcome;
	finishing;
	silence;
	signal = this.abort.signal;
	playback;
	turn;
	constructor(turn, output, onClosed) {
		this.output = output;
		this.onClosed = onClosed;
		this.turn = Object.freeze({ ...turn });
		this.playback = output.playback.openGroup(`${turn.sessionId}:${turn.turnId}`);
	}
	get closed() {
		return this.outcome !== void 0;
	}
	openSpeech(settings) {
		if (this.sealed) throw new Error("Response is closed to new speech");
		if (settings.deadlineMs !== void 0 && (!Number.isFinite(settings.deadlineMs) || settings.deadlineMs < 0)) throw new Error("Speech deadline must be finite and nonnegative");
		const previous = this.ordered;
		const stream = new SpeechStream(this, settings.purpose, this.output);
		this.ordered = Promise.all([previous, stream.done]);
		this.streams.push(stream);
		stream.start(previous, settings.deadlineMs);
		return stream;
	}
	finish() {
		if (this.finishing) return this.finishing;
		this.sealed = true;
		this.streams.forEach((stream) => stream.end());
		this.finishing = (async () => {
			const results = await Promise.all(this.streams.map((stream) => stream.done));
			const receipt = await (this.silence ?? this.playback.finish());
			this.outcome ??= receipt.status === "failed" || results.some((result) => result.status === "failed") ? "failed" : "finished";
			return this.outcome;
		})();
		this.finishing.then(this.onClosed, this.onClosed);
		return this.finishing;
	}
	cancel(reason) {
		if (this.closed) return;
		this.outcome = "cancelled";
		this.stop(reason, 0);
		this.finish().catch(() => {});
	}
	/** Triggering workflow: VoiceController.interrupt → close response delivery → fade playback and abort all producers. */
	interrupt(cause, fadeMs) {
		if (!this.closed) {
			this.outcome = "interrupted";
			this.stop(cause, fadeMs);
			this.finish().catch(() => {});
		}
		return this.silence ?? this.playback.finish();
	}
	stop(reason, fadeMs) {
		this.sealed = true;
		this.silence = this.playback.stop({ fadeMs });
		this.abort.abort(reason);
		this.streams.forEach((stream) => stream.cancel(reason));
	}
};
//#endregion
//#region src/voice/plugins/task-lifetime.ts
/** One callback owns its timeout, cancellation signal, and settlement. */
var TaskLifetime = class {
	abort = new AbortController();
	completion = Promise.withResolvers();
	timer;
	closed = false;
	signal = this.abort.signal;
	done = this.completion.promise;
	constructor(timeoutMs) {
		if (timeoutMs !== void 0) {
			if (!Number.isFinite(timeoutMs) || timeoutMs < 0) throw new Error("Plugin timeout must be finite and nonnegative");
			this.timer = setTimeout(() => this.stop({
				status: "failed",
				error: /* @__PURE__ */ new Error("Plugin task timed out")
			}), timeoutMs);
		}
	}
	stop(outcome) {
		if (this.closed) return;
		this.closed = true;
		clearTimeout(this.timer);
		if (outcome.status !== "finished") this.abort.abort(outcome);
		this.completion.resolve(outcome);
	}
	async run(callback) {
		Promise.resolve().then(() => this.closed ? void 0 : callback()).then(() => this.stop({ status: "finished" }), (cause) => this.stop({
			status: "failed",
			error: errorFromCause(cause, "Plugin callback failed")
		}));
		return this.done;
	}
};
//#endregion
//#region src/voice/plugins/input-plugins.ts
var InputSubscription = class {
	owner;
	registration;
	completion = Promise.withResolvers();
	pending = [];
	active;
	closed = false;
	ending = false;
	lastBasis = /* @__PURE__ */ new Map();
	unsubscribe = () => {};
	done = this.completion.promise;
	constructor(owner, registration) {
		this.owner = owner;
		this.registration = registration;
	}
	start() {
		if (this.closed) return;
		if (this.registration.kind === "task") {
			const task = new TaskLifetime(this.registration.settings.timeoutMs);
			this.active = task;
			const selection = this.registration.settings.selection;
			const run = this.registration.run;
			task.run(() => run({
				signal: task.signal,
				controls: this.owner.controls(task),
				cancel: (reason) => task.stop({
					status: "cancelled",
					reason
				}),
				fail: (error) => task.stop({
					status: "failed",
					error
				}),
				untilTranscriptionEnded: () => this.owner.wait(task, selection, false),
				untilDependenciesSettled: () => this.owner.wait(task, selection, true)
			})).then((outcome) => this.settle(outcome));
			return;
		}
		this.unsubscribe = this.owner.input.subscribe(() => this.refresh());
		this.refresh();
	}
	/** Triggering workflow: SpeechInput publication → selected snapshot revisions → queued plugin tasks. */
	refresh() {
		if (this.closed || this.ending || this.registration.kind !== "subscribe") return;
		const settings = this.registration.settings;
		const targets = typeof settings.scope === "object" ? this.owner.input.transcript.raw.segments.map((segment) => segment.id) : [void 0];
		for (const target of targets) {
			const snapshot = this.owner.snapshot(settings, target);
			const basis = this.owner.input.basis(snapshot);
			if (basis === this.lastBasis.get(target)) continue;
			this.lastBasis.set(target, basis);
			if (settings.scheduling === "latest") {
				const index = this.pending.findIndex((item) => item.transcript.targetSegmentId === target);
				if (index >= 0) this.pending.splice(index, 1);
			}
			this.pending.push(snapshot);
		}
		this.run();
	}
	async run() {
		if (this.active || this.closed || this.registration.kind !== "subscribe") return;
		while (this.pending.length && !this.closed) {
			const snapshot = this.pending.shift();
			const task = new TaskLifetime(this.registration.settings.timeoutMs);
			this.active = task;
			const run = this.registration.run;
			const outcome = await task.run(() => run({
				...this.owner.view(task, this.registration.kind === "subscribe" ? this.registration.settings : this.registration.settings.selection, snapshot),
				controls: this.owner.controls(task),
				signal: task.signal,
				cancel: (reason) => task.stop({
					status: "cancelled",
					reason
				}),
				fail: (error) => task.stop({
					status: "failed",
					error
				})
			}));
			this.active = void 0;
			if (outcome.status === "failed") {
				this.settle(outcome);
				return;
			}
		}
		if (this.ending) this.settle({ status: "finished" });
	}
	end() {
		if (this.registration.kind === "task") return;
		this.ending = true;
		this.unsubscribe();
		if (!this.active && !this.pending.length) this.settle({ status: "finished" });
	}
	cancel(reason) {
		this.settle({
			status: "cancelled",
			reason
		});
	}
	fail(error) {
		this.settle({
			status: "failed",
			error
		});
	}
	settle(outcome) {
		if (this.closed) return;
		this.closed = true;
		this.unsubscribe();
		this.pending.length = 0;
		this.active?.stop(outcome);
		if (outcome.status === "failed") this.owner.report(this.registration.kind === "task" ? "task" : "subscription", outcome.error);
		this.completion.resolve(outcome);
	}
};
var PluginInput = class {
	installation;
	input;
	group;
	abort = new AbortController();
	cleanups = [];
	subscriptions = [];
	registering = true;
	disposed;
	state = /* @__PURE__ */ new Map();
	done = Promise.withResolvers();
	dependencies;
	constructor(installation, input, group) {
		this.installation = installation;
		this.input = input;
		this.group = group;
		this.dependencies = new Set(installation.settings.dependsOn);
	}
	install() {
		const scope = {
			inputId: this.input.id,
			sessionId: this.input.sessionId,
			signal: this.abort.signal,
			state: this.state,
			subscribe: (settings, run) => this.register({
				kind: "subscribe",
				settings,
				run
			}),
			task: (settings, run) => this.register({
				kind: "task",
				settings,
				run
			}),
			cancel: (reason) => void this.dispose({
				status: "cancelled",
				reason
			}),
			fail: (error) => {
				this.report("task", error);
				this.dispose({
					status: "failed",
					error
				});
			},
			onDispose: (cleanup) => this.cleanups.push(cleanup)
		};
		try {
			for (const install of this.installation.installers) {
				const cleanup = install(scope);
				if (cleanup !== void 0 && typeof cleanup !== "function") throw new Error("Plugin input setup must be synchronous");
				if (cleanup) this.cleanups.push(cleanup);
			}
		} catch (cause) {
			const error = errorFromCause(cause, "Plugin input setup failed");
			this.report("setup", error);
			this.dispose({
				status: "failed",
				error
			});
		} finally {
			this.registering = false;
		}
	}
	register(registration) {
		if (!this.registering || this.abort.signal.aborted) throw new Error("Register plugin work during input setup");
		const selection = registration.kind === "subscribe" ? registration.settings : registration.settings.selection;
		if (selection.transcript === "corrected" || typeof selection.scope === "object" && selection.scope.neighborTranscript === "corrected") for (const name of selection.correctionPlugins ?? this.group.patchPlugins) {
			if (!this.group.patchPlugins.includes(name)) throw new Error(`Unknown correction plugin: ${name}`);
			this.dependencies.add(name);
		}
		for (const selected of selection.context ?? []) {
			if (!(this.installation.settings.dependsOn ?? []).includes(selected.plugin) || selected.plugin === this.installation.plugin.name) throw new Error("Selected context requires an upstream plugin dependency");
			if (selected.scope === "target-segment" && typeof selection.scope !== "object") throw new Error("Segment context requires a segment selection");
		}
		if (typeof selection.scope === "object" && (!Number.isSafeInteger(selection.scope.neighbors) || selection.scope.neighbors < 0)) throw new Error("Neighbor count must be a nonnegative integer");
		const grace = registration.settings.waitForSubmissionMs;
		if (grace !== void 0 && (!Number.isFinite(grace) || grace < 0)) throw new Error("Submission grace must be finite and nonnegative");
		const subscription = new InputSubscription(this, registration);
		this.subscriptions.push(subscription);
		return subscription;
	}
	start() {
		if (!this.abort.signal.aborted) this.subscriptions.forEach((subscription) => subscription.start());
	}
	snapshot(selection, targetSegmentId) {
		return this.input.snapshot(selection, selection.correctionPlugins ?? this.group.patchPlugins, targetSegmentId);
	}
	view(task, selection, snapshot = this.snapshot(selection)) {
		const basis = this.input.basis(snapshot);
		const current = () => this.input.basis(this.snapshot(selection, snapshot.transcript.targetSegmentId)) === basis;
		const live = () => !task.closed && !this.abort.signal.aborted && !this.input.closed;
		const closed = {
			status: "rejected",
			reason: "closed"
		};
		return {
			snapshot,
			context: {
				set: (key, value) => live() ? this.input.publish(this.installation.plugin.name, key, value, current, snapshot.transcript.targetSegmentId) : closed,
				delete: (key) => live() ? this.input.deleteContext(this.installation.plugin.name, key, current, snapshot.transcript.targetSegmentId) : closed
			},
			patch: (proposal) => {
				if (!live()) return closed;
				if (!this.installation.settings.grants?.includes("transcript-patch") || selection.transcript !== "raw") return {
					status: "rejected",
					reason: "denied"
				};
				if (proposal.edits.some((edit) => !snapshot.transcript.segments.some((segment) => segment.id === edit.segmentId))) return {
					status: "rejected",
					reason: "denied"
				};
				return this.input.patch(this.installation.plugin.name, proposal, current);
			}
		};
	}
	controls(task) {
		return this.installation.controls(() => !task.closed && !this.abort.signal.aborted && !this.input.closed);
	}
	async wait(task, selection, dependencies) {
		const stopped = task.done.then((outcome) => outcome.status === "failed" ? outcome : {
			status: "cancelled",
			reason: "Plugin task closed"
		});
		const ended = await Promise.race([this.group.transcription.promise, stopped]);
		if (ended.status !== "finished") return ended;
		if (dependencies) for (const name of this.dependencies) {
			const upstream = this.group.scopes.get(name);
			if (!upstream) return {
				status: "failed",
				error: /* @__PURE__ */ new Error(`Missing upstream plugin: ${name}`)
			};
			const outcome = await Promise.race([upstream.done.promise, stopped]);
			if (outcome.status !== "finished") return outcome;
		}
		if (task.closed || this.abort.signal.aborted || this.input.closed) return {
			status: "cancelled",
			reason: "Plugin task closed"
		};
		return {
			status: "finished",
			value: this.view(task, selection)
		};
	}
	async finish() {
		const timers = /* @__PURE__ */ new Map();
		for (const subscription of this.subscriptions) {
			const grace = subscription.registration.settings.waitForSubmissionMs ?? 0;
			if (grace > 0) timers.set(subscription, setTimeout(() => subscription.cancel("Submission grace ended"), grace));
		}
		for (const name of this.dependencies) {
			const upstream = this.group.scopes.get(name);
			if ((upstream ? await upstream.done.promise : void 0)?.status !== "finished") {
				this.subscriptions.forEach((subscription) => subscription.cancel("Upstream plugin did not finish"));
				break;
			}
		}
		const results = await Promise.all(this.subscriptions.map(async (subscription) => {
			subscription.end();
			if (!(subscription.registration.settings.waitForSubmissionMs ?? 0)) subscription.cancel("Submission does not wait for this task");
			const result = await subscription.done;
			clearTimeout(timers.get(subscription));
			return result;
		}));
		this.done.resolve(results.find((result) => result.status === "failed") ?? results.find((result) => result.status === "cancelled") ?? { status: "finished" });
	}
	report(stage, error) {
		this.installation.report(stage, error, this.input.id);
	}
	failDependency(error) {
		this.report("dependency", error);
		this.dispose({
			status: "failed",
			error
		});
	}
	dispose(outcome) {
		if (this.disposed) return this.disposed;
		this.abort.abort(outcome);
		this.subscriptions.forEach((subscription) => outcome.status === "failed" ? subscription.fail(outcome.error) : subscription.cancel("Plugin input closed"));
		this.input.removePlugin(this.installation.plugin.name);
		this.done.resolve(outcome);
		this.disposed = (async () => {
			for (const cleanup of this.cleanups.reverse()) try {
				await cleanup();
			} catch (cause) {
				this.report("cleanup", errorFromCause(cause, "Plugin cleanup failed"));
			}
		})();
		return this.disposed;
	}
};
/** Owns a fixed set of plugin installations for one accepted input. Later installations apply to future inputs. */
var InputPlugins = class {
	input;
	scopes = /* @__PURE__ */ new Map();
	transcription = Promise.withResolvers();
	patchPlugins;
	constructor(input, installations) {
		this.input = input;
		this.patchPlugins = installations.filter((installation) => installation.settings.grants?.includes("transcript-patch")).map((installation) => installation.plugin.name);
		for (const installation of installations) {
			const scope = new PluginInput(installation, input, this);
			this.scopes.set(installation.plugin.name, scope);
			installation.inputs.add(scope);
		}
		for (const scope of this.scopes.values()) scope.install();
		const visited = /* @__PURE__ */ new Set();
		const active = /* @__PURE__ */ new Set();
		const visit = (name) => {
			if (active.has(name)) throw new Error(`Plugin dependency feedback: ${[...active, name].join(" → ")}`);
			if (visited.has(name)) return;
			active.add(name);
			const scope = this.scopes.get(name);
			if (!scope) throw new Error(`Missing plugin input dependency: ${name}`);
			for (const dependency of scope.dependencies) visit(dependency);
			active.delete(name);
			visited.add(name);
		};
		for (const [name, scope] of this.scopes) try {
			visit(name);
		} catch (cause) {
			scope.failDependency(errorFromCause(cause, "Plugin dependency failed"));
			active.clear();
		}
		for (const scope of this.scopes.values()) scope.start();
	}
	async finish() {
		this.transcription.resolve({ status: "finished" });
		await Promise.resolve();
		await Promise.all([...this.scopes.values()].map((scope) => scope.finish()));
	}
	async close(outcome) {
		this.transcription.resolve(outcome);
		await Promise.all([...this.scopes.values()].map(async (scope) => {
			await scope.dispose(outcome);
			scope.installation.inputs.delete(scope);
		}));
	}
};
//#endregion
//#region src/voice/plugins/registry.ts
var PluginInstallation = class {
	host;
	actions;
	plugin;
	settings;
	abort = new AbortController();
	inputs = /* @__PURE__ */ new Set();
	installers = [];
	cleanups = [];
	detectors = /* @__PURE__ */ new Set();
	disposing;
	constructor(host, actions, plugin, settings) {
		this.host = host;
		this.actions = actions;
		this.plugin = plugin;
		this.settings = settings;
	}
	report(stage, error, inputId) {
		try {
			if (this.settings.onError) this.settings.onError({
				plugin: this.plugin.name,
				stage,
				error,
				...inputId ? { inputId } : {}
			});
			else this.actions.reportError(`plugin:${this.plugin.name}:${stage}`, error);
		} catch (cause) {
			this.actions.reportError("plugin-diagnostic", cause);
		}
	}
	controls(live) {
		const allows = (grant) => live() && !this.abort.signal.aborted && !!this.settings.grants?.includes(grant);
		const control = (attempt) => ({
			id: attempt.id,
			sessionId: attempt.sessionId,
			noteActivity: (evidence) => allows("input-control") && attempt.noteActivity(evidence),
			end: () => allows("input-control") ? {
				status: "accepted",
				done: attempt.end()
			} : { status: "denied" },
			cancel: (reason) => allows("cancel-input") ? this.actions.cancelInput(attempt.id, reason) : "denied",
			detectEnd: (options, detector) => {
				if (!allows("input-control")) return { status: "denied" };
				const observer = attempt.detectEnd(options, detector);
				this.detectors.add(observer);
				observer.done.then(() => this.detectors.delete(observer));
				return {
					status: "installed",
					observer
				};
			}
		});
		return {
			activeInput: () => {
				const attempt = this.actions.activeInput();
				return attempt && allows("input-control") ? control(attempt) : void 0;
			},
			beginInput: (settings) => allows("input-control") ? {
				status: "started",
				input: control(this.actions.beginInput(settings))
			} : { status: "denied" },
			cancelInput: (id, reason) => allows("cancel-input") ? this.actions.cancelInput(id, reason) : "denied",
			interrupt: (turns, cause) => allows("interrupt-turns") ? {
				status: "started",
				interruption: this.actions.interrupt({
					turns,
					cause
				})
			} : { status: "denied" }
		};
	}
	setup() {
		const scope = {
			signal: this.abort.signal,
			state: /* @__PURE__ */ new Map(),
			onSpeechInput: (install) => this.installers.push(install),
			onDispose: (cleanup) => this.cleanups.push(cleanup),
			cancel: () => void this.dispose(),
			fail: (error) => {
				this.report("setup", error);
				this.dispose();
			},
			observeAudio: (settings, run) => {
				const completion = Promise.withResolvers();
				const scope = createScope(settings.signal ? AbortSignal.any([this.abort.signal, settings.signal]) : this.abort.signal);
				let generation;
				const detect = async (window, signal) => {
					const task = new TaskLifetime();
					signal.addEventListener("abort", () => task.stop({
						status: "cancelled",
						reason: "Audio observer closed"
					}), {
						once: true,
						signal: task.signal
					});
					const outcome = await task.run(() => run({
						window,
						signal: task.signal,
						controls: this.controls(() => !task.closed),
						publish: (value) => {
							if (task.closed || signal.aborted || this.abort.signal.aborted) return {
								status: "rejected",
								reason: "closed"
							};
							this.settings.onAudioEvidence?.({
								plugin: this.plugin.name,
								range: window.range,
								value
							});
							return { status: "applied" };
						},
						cancel: (reason) => task.stop({
							status: "cancelled",
							reason
						}),
						fail: (error) => task.stop({
							status: "failed",
							error
						})
					}));
					if (outcome.status === "failed") throw outcome.error;
				};
				const start = () => {
					generation?.close("Audio input replaced");
					const audio = this.actions.audio();
					if (!audio || scope.signal.aborted) return;
					const current = scope.child();
					generation = current;
					observe(audio, {
						...settings,
						signal: current.signal
					}, detect, () => {}).done.then((outcome) => {
						if (current.signal.aborted) return;
						if (outcome.status === "failed") this.report("subscription", outcome.error);
						if (outcome.status !== "cancelled") completion.resolve(outcome);
						scope.close();
					});
				};
				const observation = {
					restart: start,
					cancel: () => void scope.close("Plugin observation cancelled")
				};
				scope.defer(() => {
					completion.resolve({
						status: "cancelled",
						reason: "Plugin observation closed"
					});
					this.host.removeAudioObservation(observation);
				});
				if (!scope.signal.aborted) {
					this.host.addAudioObservation(observation);
					start();
				}
				return {
					done: completion.promise,
					cancel: (reason) => void scope.close(reason)
				};
			}
		};
		try {
			const cleanup = this.plugin.setup(scope);
			if (cleanup !== void 0 && typeof cleanup !== "function") throw new Error("Plugin setup must be synchronous");
			if (cleanup) this.cleanups.push(cleanup);
		} catch (cause) {
			scope.fail(errorFromCause(cause, "Plugin setup failed"));
		}
	}
	dispose() {
		if (this.disposing) return this.disposing;
		this.abort.abort();
		this.host.remove(this);
		for (const detector of this.detectors) detector.cancel("Plugin disposed");
		this.detectors.clear();
		this.disposing = (async () => {
			await Promise.all([...this.inputs].map((scope) => scope.dispose({
				status: "cancelled",
				reason: "Plugin disposed"
			})));
			const errors = [];
			for (const cleanup of this.cleanups.reverse()) try {
				await cleanup();
			} catch (cause) {
				errors.push(errorFromCause(cause, "Plugin cleanup failed"));
			}
			return errors.length ? {
				status: "failed",
				errors
			} : { status: "disposed" };
		})();
		return this.disposing;
	}
};
/** Owns trusted installations and their future input registrations. */
var VoicePlugins = class {
	actions;
	installations = /* @__PURE__ */ new Map();
	audioObservations = /* @__PURE__ */ new Set();
	constructor(actions) {
		this.actions = actions;
	}
	/** Source replacement cancels this controller's observations, including pending source acquisition. */
	replaceAudio() {
		for (const observation of this.audioObservations) observation.restart();
	}
	addAudioObservation(observation) {
		this.audioObservations.add(observation);
	}
	removeAudioObservation(observation) {
		this.audioObservations.delete(observation);
	}
	use(plugin, settings = {}) {
		if (this.installations.has(plugin.name)) throw new Error(`Plugin already installed: ${plugin.name}`);
		for (const dependency of settings.dependsOn ?? []) if (!this.installations.has(dependency)) throw new Error(`Missing plugin dependency: ${dependency}`);
		const installation = new PluginInstallation(this, this.actions, plugin, settings);
		this.installations.set(plugin.name, installation);
		installation.setup();
		return installation;
	}
	input(input) {
		return new InputPlugins(input, [...this.installations.values()]);
	}
	remove(installation) {
		this.installations.delete(installation.plugin.name);
		for (const input of installation.inputs) for (const scope of input.group.scopes.values()) if (scope.dependencies.has(installation.plugin.name)) scope.failDependency(/* @__PURE__ */ new Error(`Plugin dependency disposed: ${installation.plugin.name}`));
		for (const dependent of this.installations.values()) if (dependent.settings.dependsOn?.includes(installation.plugin.name)) dependent.dispose();
	}
	async close() {
		await Promise.all([...this.installations.values()].map((installation) => installation.dispose()));
	}
};
//#endregion
//#region src/voice/turn.ts
/** Encodes both identity fields without delimiter collisions across sessions. */
function turnKey(turn) {
	return JSON.stringify([turn.sessionId, turn.turnId]);
}
//#endregion
//#region src/voice/controller.ts
/** Coordinates input admission and downstream completion without owning browser or provider APIs. */
var VoiceController = class {
	options;
	plugins = new VoicePlugins({
		activeInput: () => this.current,
		audio: () => this.audioInput,
		beginInput: (options) => this.beginInput(options),
		cancelInput: (id, reason) => this.cancelInput(id, reason),
		interrupt: (options) => this.interrupt(options),
		reportError: (stage, cause) => this.reportError(stage, cause)
	});
	inputListeners = /* @__PURE__ */ new Set();
	current;
	attempts = /* @__PURE__ */ new Map();
	audioInput;
	closing;
	responses = /* @__PURE__ */ new Map();
	/**
	* Turn keys stay here after their response closes, so a late producer cannot reopen finished speech.
	* The set holds one short key per turn and clears when the controller closes.
	*/
	usedTurns = /* @__PURE__ */ new Set();
	interruptions = /* @__PURE__ */ new Map();
	/** Every attempt receives the same narrow view of this controller. */
	inputOwner;
	constructor(options) {
		this.options = options;
		this.audioInput = options.audio;
		this.inputOwner = {
			options,
			audio: () => this.audioInput,
			interrupt: (request) => this.interrupt(request),
			createPlugins: (input) => this.plugins.input(input),
			seal: (attempt) => this.sealInput(attempt),
			reportError: (stage, error) => this.reportError(stage, error)
		};
	}
	get audio() {
		return this.audioInput;
	}
	/** Diagnostics cannot take ownership of a domain operation's completion. */
	reportError(stage, cause) {
		const error = errorFromCause(cause, "Voice operation failed");
		try {
			if (this.options.onError) this.options.onError({
				stage,
				error
			});
			else console.error(`[VoiceController] ${stage}`, error);
		} catch (diagnosticError) {
			console.error("[VoiceController] Diagnostic handler failed", diagnosticError);
		}
	}
	get activeInput() {
		return this.current;
	}
	use(plugin, settings) {
		if (this.closing) throw new Error("Voice controller is closed");
		return this.plugins.use(plugin, settings);
	}
	/** Observers see pending input immediately, including attempts opened by signal plugins. */
	onInput(listener) {
		if (this.closing) throw new Error("Voice controller is closed");
		this.inputListeners.add(listener);
		return () => this.inputListeners.delete(listener);
	}
	beginInput(options) {
		if (this.closing) throw new Error("Voice controller is closed");
		this.current?.cancel("Replaced by a new input");
		const attempt = new SpeechInputAttempt(this.inputOwner, options);
		this.current = attempt;
		this.attempts.set(attempt.id, attempt);
		for (const listener of this.inputListeners) try {
			listener(attempt);
		} catch (error) {
			this.reportError("input-observer", error);
		}
		attempt.done.then(() => this.attempts.delete(attempt.id));
		attempt.start();
		return attempt;
	}
	cancelInput(inputId, reason) {
		const attempt = this.attempts.get(inputId);
		if (!attempt) return "closed";
		attempt.cancel(reason);
		return "cancelled";
	}
	updateSpeakerEvidence(inputId, evidence) {
		const attempt = this.attempts.get(inputId);
		if (!attempt?.input || attempt.input.closed) return {
			status: "rejected",
			reason: "closed"
		};
		if (!attempt.acceptsEvidence(evidence.range)) return {
			status: "rejected",
			reason: "stale"
		};
		return attempt.input.updateSpeakers(evidence.value);
	}
	correctInput(inputId, edits) {
		const input = this.attempts.get(inputId)?.input;
		if (!input || input.closed) return {
			status: "rejected",
			reason: "closed"
		};
		return input.patch("user", {
			edits,
			evidenceIds: []
		}, () => true, true);
	}
	openResponse(turn) {
		if (this.closing) throw new Error("Voice controller is closed");
		const key = turnKey(turn);
		const existing = this.responses.get(key);
		if (existing) return existing;
		if (this.usedTurns.has(key)) throw new Error("Response turn identity is already used");
		if (!this.options.speech) throw new Error("Speech output is not configured");
		const response = new VoiceResponse(turn, this.options.speech(turn), () => this.responses.delete(key));
		this.usedTurns.add(key);
		this.responses.set(key, response);
		return response;
	}
	interrupt(options) {
		const id = nanoid$1();
		const fadeMs = this.options.fadeMs ?? 100;
		const pending = [...new Map(options.turns.map((turn) => [turnKey(turn), Object.freeze({ ...turn })])).entries()].map(([key, turn]) => {
			const response = this.responses.get(key);
			let record = this.interruptions.get(key);
			if (!record && response && !response.closed) {
				record = {
					eventId: nanoid$1(),
					turn,
					cause: options.cause,
					silence: response.interrupt(options.cause, fadeMs)
				};
				this.interruptions.set(key, record);
			}
			const silence = record?.silence ?? response?.interrupt(options.cause, fadeMs);
			if (silence) {
				const result = silence.then((playback) => ({
					turn,
					status: playback.status,
					playback,
					...playback.status === "failed" ? { error: playback.error } : {}
				}));
				return {
					record,
					result,
					known: true
				};
			}
			if (this.usedTurns.has(key)) return {
				record,
				result: Promise.resolve({
					turn,
					status: "silent"
				}),
				known: true
			};
			return {
				record,
				result: Promise.resolve({
					turn,
					status: "failed",
					error: /* @__PURE__ */ new Error("Unknown response turn")
				}),
				known: false
			};
		});
		const silenced = Promise.all(pending.map((target) => target.result));
		const notifications = pending.flatMap((target) => target.record ? [this.notifyInterruption(target.record)] : []);
		return {
			id,
			silenced,
			done: Promise.all(notifications).then((results) => {
				return {
					status: pending.some((target) => !target.known) || results.some((item) => item.status === "failed") ? "failed" : "recorded",
					notifications: results
				};
			})
		};
	}
	/** Triggering workflow: {@link interrupt} → per-turn record → recordInterruption persistence acknowledgment. */
	notifyInterruption(record) {
		if (record.notification) return record.notification;
		const pending = (async () => {
			const playback = await record.silence;
			try {
				if (!this.options.recordInterruption) return {
					turn: record.turn,
					eventId: record.eventId,
					status: "failed"
				};
				const receipt = await this.options.recordInterruption({
					eventId: record.eventId,
					turn: record.turn,
					cause: record.cause,
					playback
				});
				return {
					turn: record.turn,
					eventId: record.eventId,
					status: receipt.status
				};
			} catch (cause) {
				this.reportError("interruption", cause);
				return {
					turn: record.turn,
					eventId: record.eventId,
					status: "failed"
				};
			}
		})();
		record.notification = pending;
		pending.then((result) => {
			if (result.status === "failed" && record.notification === pending) record.notification = void 0;
			else if (result.status !== "failed" && record.notification === pending) this.interruptions.delete(turnKey(record.turn));
		});
		return pending;
	}
	/**
	* Switches future inputs and plugin observations to another shared input, for example after a device change.
	* Active attempts cancel, because their frame coordinates belong to the old source. Responses continue.
	*/
	replaceAudio(audio) {
		if (this.closing) throw new Error("Voice controller is closed");
		if (audio === this.audioInput) return;
		for (const attempt of this.attempts.values()) attempt.cancel("Audio input replaced");
		this.audioInput = audio;
		this.plugins.replaceAudio();
	}
	sealInput(attempt) {
		if (this.current === attempt) this.current = void 0;
	}
	close() {
		if (this.closing) return this.closing;
		for (const attempt of this.attempts.values()) attempt.cancel("Voice controller closed");
		this.inputListeners.clear();
		for (const response of this.responses.values()) response.cancel("Voice controller closed");
		const responsesClosed = [...this.responses.values()].map((response) => response.finish());
		this.closing = Promise.all([this.plugins.close(), ...responsesClosed]).then(() => {}).finally(() => {
			this.responses.clear();
			this.interruptions.clear();
			this.usedTurns.clear();
		});
		return this.closing;
	}
};
//#endregion
export { SpeechInput, SpeechInputAttempt, SpeechStream, VoiceController, VoiceResponse, buildContextPromptMessage, categorizeResponse, chatContentToInputSegments, chatMessagesToTurns, conversationToChatMessages, createChatHooks, createChatOrchestratorRuntime, createContextRegistry, createStreamingCategorizer, formatContextPromptText, formatTimePrefix, isContentArrayRelatedError, isToolRelatedError, mergeLoadedSessionMessages, modelKey, renderConversationPreview, streamFrom, turnKey, useLlmmarkerParser };
